"use client";

import { useEffect, useMemo, useState, useRef, useCallback, useTransition } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import Label from "./ui/label";
import { Plus, Trash2, Check, RefreshCw, Loader2, Pencil, MapPin, X, Star, Building2, ChevronDown, Globe, Search, Users } from "lucide-react";
import { getScrapeProgress } from "../apis/userApiService";
import SingleSelectDropdown from "./ui/singleselect-dropdown";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "./ui/accordion";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Checkbox } from "./ui/checkbox";
import { Textarea } from "./ui/textarea";
import { toast } from "react-hot-toast";

import {
    getMyProfile,
    updateProfile as updateProfileApi,
    scrapeDoctors as syncDoctorsApi,
} from "../apis/userApiService";

import { Country, State, City } from 'country-state-city';
import { filterAndSortList } from "../utils/searchUtils";

// ── Searchable Combobox with free-text insert ─────────────────────────────
function SearchableCombobox({
    options = [],
    value = "",
    onChange,
    onBlur,
    placeholder = "Search or type...",
    disabled = false,
    onDisabledClick,
    itemLabel = "practice",
}) {
    const [open, setOpen] = useState(false);
    const [inputVal, setInputVal] = useState(value ?? "");
    const [searchQuery, setSearchQuery] = useState("");
    const [highlightedIndex, setHighlightedIndex] = useState(-1);
    const containerRef = useRef(null);
    const inputRef = useRef(null);
    const listRef = useRef(null);

    useEffect(() => {
        setInputVal(value ?? "");
        if (!value) {
            setSearchQuery("");
        }
    }, [value]);

    const allUniqueOptions = useMemo(() => {
        const opts = Array.isArray(options) ? options : [];
        const list = [];
        const seen = new Set();
        opts.forEach(o => {
            const val = typeof o === "string" ? o : (o?.label || o?.value || "");
            if (!val) return;
            const key = val.trim().toLowerCase();
            if (!seen.has(key)) {
                seen.add(key);
                list.push(val.trim());
            }
        });
        return list;
    }, [options]);

    const filtered = useMemo(() => {
        if (disabled) return [];
        const q = (searchQuery || "").trim();
        if (!q) return allUniqueOptions;
        return filterAndSortList(allUniqueOptions, q);
    }, [allUniqueOptions, searchQuery, disabled]);

    useEffect(() => {
        const handler = (e) => {
            if (containerRef.current && !containerRef.current.contains(e.target)) {
                setOpen(false);
                setSearchQuery("");
                setHighlightedIndex(-1);
            }
        };
        document.addEventListener("mousedown", handler);
        return () => document.removeEventListener("mousedown", handler);
    }, []);

    useEffect(() => {
        if (open) {
            if (searchQuery.trim()) {
                setHighlightedIndex(filtered.length > 0 ? 0 : -1);
            } else {
                const cur = (inputVal || "").trim().toLowerCase();
                const matchIdx = filtered.findIndex(o => o.toLowerCase() === cur);
                setHighlightedIndex(matchIdx >= 0 ? matchIdx : (filtered.length > 0 ? 0 : -1));
            }
        } else {
            setHighlightedIndex(-1);
        }
    }, [open, searchQuery, filtered]);

    useEffect(() => {
        if (open && listRef.current && highlightedIndex >= 0) {
            const itemEl = listRef.current.children[highlightedIndex];
            if (itemEl && typeof itemEl.scrollIntoView === "function") {
                itemEl.scrollIntoView({ block: "nearest" });
            }
        }
    }, [highlightedIndex, open]);

    const handleSelect = (opt) => {
        if (disabled) return;
        setInputVal(opt);
        setSearchQuery("");
        onChange(opt);
        setOpen(false);
        onBlur?.(opt);
    };

    const handleClear = () => {
        if (disabled) return;
        setInputVal("");
        setSearchQuery("");
        onChange("");
        onBlur?.("");
        setOpen(true);
        inputRef.current?.focus();
    };

    const handleInputChange = (e) => {
        if (disabled) return;
        const v = e.target.value;
        setInputVal(v);
        setSearchQuery(v);
        setOpen(true);
        onChange(v);
    };

    const handleBlur = () => {
        setTimeout(() => {
            if (!open) onBlur?.(inputVal);
        }, 150);
    };

    const handleKeyDown = (e) => {
        if (e.key === "ArrowDown") {
            e.preventDefault();
            if (!open) {
                setOpen(true);
            } else if (filtered.length > 0) {
                setHighlightedIndex(prev => (prev < filtered.length - 1 ? prev + 1 : 0));
            }
        } else if (e.key === "ArrowUp") {
            e.preventDefault();
            if (!open) {
                setOpen(true);
            } else if (filtered.length > 0) {
                setHighlightedIndex(prev => (prev > 0 ? prev - 1 : filtered.length - 1));
            }
        } else if (e.key === "Enter") {
            e.preventDefault();
            if (open && highlightedIndex >= 0 && highlightedIndex < filtered.length) {
                handleSelect(filtered[highlightedIndex]);
            } else if (filtered.length > 0) {
                handleSelect(filtered[0]);
            } else if (inputVal.trim()) {
                handleSelect(inputVal.trim());
            }
        } else if (e.key === "Escape") {
            setOpen(false);
            setSearchQuery("");
        }
    };

    return (
        <div
            ref={containerRef}
            className={`relative w-full mt-2 ${open ? "z-50" : ""} ${disabled ? "cursor-not-allowed" : ""}`}
            onClick={() => {
                if (disabled && onDisabledClick) {
                    onDisabledClick();
                }
            }}
        >
            <div className="relative flex items-center">
                <Input
                    ref={inputRef}
                    value={inputVal}
                    disabled={disabled}
                    onChange={handleInputChange}
                    onFocus={(e) => {
                        if (!disabled) {
                            setOpen(true);
                            setTimeout(() => {
                                e.target?.select();
                            }, 0);
                        }
                    }}
                    onClick={() => {
                        if (!disabled && !open) {
                            setSearchQuery("");
                            setOpen(true);
                        }
                    }}
                    onKeyDown={handleKeyDown}
                    onBlur={handleBlur}
                    placeholder={placeholder}
                    className={`h-11 w-full rounded-xl border border-slate-200 bg-white pl-4 pr-16 text-sm text-slate-900 shadow-sm transition-all focus:border-blue-400 focus:ring-1 focus:ring-blue-400 ${disabled ? "bg-slate-100 text-slate-400 cursor-not-allowed border-slate-200" : ""
                        }`}
                />

                {/* Action Buttons: Clear (X) + Dropdown Toggle (Chevron) */}
                {!disabled && (
                    <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-0.5 z-10">
                        {Boolean(inputVal) && (
                            <button
                                type="button"
                                title="Clear"
                                className="h-7 w-7 rounded-lg flex items-center justify-center text-slate-400 hover:text-red-500 hover:bg-slate-100 transition-colors cursor-pointer"
                                onMouseDown={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    handleClear();
                                }}
                            >
                                <X className="h-4 w-4" />
                            </button>
                        )}
                        <button
                            type="button"
                            title={open ? "Close options" : "Show all options"}
                            className="h-7 w-7 rounded-lg flex items-center justify-center text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-colors cursor-pointer"
                            onMouseDown={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                setSearchQuery("");
                                setOpen(prev => !prev);
                                inputRef.current?.focus();
                            }}
                        >
                            <ChevronDown className={`h-4 w-4 transition-transform duration-200 ${open ? "rotate-180" : ""}`} />
                        </button>
                    </div>
                )}
            </div>

            {!disabled && open && (
                <div ref={listRef} className="absolute z-50 left-0 right-0 mt-1 max-h-64 overflow-y-auto rounded-xl border border-slate-200 bg-white shadow-xl custom-scrollbar py-1">
                    {filtered.length > 0 ? (
                        <>
                            {filtered.map((opt, idx) => {
                                const isSelected = Boolean(inputVal && opt.toLowerCase() === inputVal.trim().toLowerCase());
                                const isHighlighted = idx === highlightedIndex;
                                return (
                                    <div
                                        key={idx}
                                        className={`cursor-pointer px-3.5 py-2 text-sm sm:text-sm flex items-center justify-between transition-colors ${isSelected && isHighlighted
                                                ? "bg-blue-100 text-blue-900 font-medium"
                                                : isHighlighted
                                                    ? "bg-blue-50 text-blue-700 font-medium"
                                                    : isSelected
                                                        ? "bg-blue-50 text-blue-900 font-medium"
                                                        : "text-slate-800 hover:bg-slate-50"
                                            }`}
                                        onMouseEnter={() => setHighlightedIndex(idx)}
                                        onMouseDown={(e) => {
                                            e.preventDefault();
                                            handleSelect(opt);
                                        }}
                                    >
                                        <span className="truncate pr-2" title={opt}>{opt}</span>
                                        {isSelected && <Check className="h-4 w-4 text-blue-600 shrink-0" />}
                                    </div>
                                );
                            })}
                            {Boolean(inputVal.trim()) && !filtered.some(f => f.toLowerCase() === inputVal.trim().toLowerCase()) && (
                                <div
                                    className="cursor-pointer px-4 py-2.5 text-xs text-blue-600 hover:bg-blue-50 font-medium flex items-center justify-between border-t border-slate-100 mt-1"
                                    onMouseDown={(e) => {
                                        e.preventDefault();
                                        handleSelect(inputVal.trim());
                                    }}
                                >
                                    <span>Use "<strong>{inputVal.trim()}</strong>" as custom {itemLabel}</span>
                                    <span className="text-xs bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded font-semibold">Select</span>
                                </div>
                            )}
                        </>
                    ) : inputVal.trim() ? (
                        <div
                            className="cursor-pointer px-4 py-2.5 text-xs text-blue-600 hover:bg-blue-50 font-medium flex items-center justify-between"
                            onMouseDown={(e) => {
                                e.preventDefault();
                                handleSelect(inputVal.trim());
                            }}
                        >
                            <span>Use "<strong>{inputVal.trim()}</strong>" as custom {itemLabel}</span>
                            <span className="text-xs bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded font-semibold">Select</span>
                        </div>
                    ) : (
                        <div className="px-4 py-3 text-xs text-slate-400 text-center italic">
                            No practice options available
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

export function PracticePartnersSettings() {
    const [practicePartners, setPracticePartners] = useState([]);
    const [syncingDoctors, setSyncingDoctors] = useState(false);
    const [scrapeJobStatus, setScrapeJobStatus] = useState(null);
    const [doctorSearchTerm, setDoctorSearchTerm] = useState("");
    const [loaded, setLoaded] = useState(false);
    const [myPracticeName, setMyPracticeName] = useState("");
    // Location data needed for syncing
    const [address, setAddress] = useState("");
    const [city, setCity] = useState("");
    const [state, setState] = useState("");
    const [zipCode, setZipCode] = useState("");
    const [country, setCountry] = useState("");

    // Multi-form state
    const [activeForm, setActiveFormRaw] = useState(1);
    const [isFormTransitioning, startFormTransition] = useTransition();
    const setActiveForm = (form) => {
        startFormTransition(() => {
            setActiveFormRaw(form);
            setVisibleCount(20); // reset pagination on tab switch
        });
    };

    // Extra dynamic locations (Location 2, 3, 4, ...)
    // extraLocations[0] = Location 2, extraLocations[1] = Location 3, etc.
    const [extraLocations, setExtraLocations] = useState([]);


    // Trusted Practice Partners & Filter state
    const [filterTab, setFilterTab] = useState("all"); // "all" | "trusted" | "same_practice" | "external"


    // Manual Provider Add State
    const [isManualAddOpen, setIsManualAddOpen] = useState(false);
    const [editingProviderOriginal, setEditingProviderOriginal] = useState(null);
    const [isSyncConfirmOpen, setIsSyncConfirmOpen] = useState(false);
    const [partnerToDelete, setPartnerToDelete] = useState(null);
    const [isDeleteAllOpen, setIsDeleteAllOpen] = useState(false);
    const [locationToDelete, setLocationToDelete] = useState(null);
    const [newProvider, setNewProvider] = useState({
        name: "",
        nickname: "",
        specialties: "",
        practice: "",
        address: "",
        city: "",
        state: "",
        country: "",
        distance: "",
        phone: "",
        languages: "",
        expertiseCount: "",
        acceptingNewPatients: false,
        isTrusted: false,
        bio: ""
    });

    const [manualSelectedCountry, setManualSelectedCountry] = useState(null);
    const [manualSelectedState, setManualSelectedState] = useState(null);

    // Location Dropdown State
    const [selectedCountry, setSelectedCountry] = useState(null);
    const [selectedState, setSelectedState] = useState(null);
    const [visibleCount, setVisibleCount] = useState(20);
    const [selectedSpecialtyFilter, setSelectedSpecialtyFilter] = useState("ALL");
    const [isLocationDetailsOpen, setIsLocationDetailsOpen] = useState(false);

    const getInitials = (nameStr) => {
        if (!nameStr || typeof nameStr !== 'string') return "Dr";
        const clean = nameStr.replace(/^(Dr\.?|Doctor)\s+/i, '').trim();
        const parts = clean.split(/\s+/);
        if (parts.length >= 2) {
            return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
        }
        return clean.slice(0, 2).toUpperCase() || "Dr";
    };

    const allCountries = useMemo(() => Country.getAllCountries().map(c => ({ label: c.name, value: c.isoCode, ...c })), []);
    const manualStates = useMemo(() => manualSelectedCountry ? State.getStatesOfCountry(manualSelectedCountry.isoCode).map(s => ({ label: s.name, value: s.isoCode, ...s })) : [], [manualSelectedCountry]);
    const manualCities = useMemo(() => manualSelectedState ? City.getCitiesOfState(manualSelectedCountry.isoCode, manualSelectedState.isoCode).map(c => ({ label: c.name, value: c.name, ...c })) : [], [manualSelectedCountry, manualSelectedState]);

    const states = useMemo(() => selectedCountry ? State.getStatesOfCountry(selectedCountry.isoCode).map(s => ({ label: s.name, value: s.isoCode, ...s })) : [], [selectedCountry]);
    const cities = useMemo(() => selectedState ? City.getCitiesOfState(selectedCountry.isoCode, selectedState.isoCode).map(c => ({ label: c.name, value: c.name, ...c })) : [], [selectedCountry, selectedState]);

    const availableSpecialties = useMemo(() => {
        let partners = [];
        const currentList = activeForm === 1
            ? practicePartners
            : extraLocations[activeForm - 2]?.practicePartners || [];
        try {
            if (typeof currentList === "string") {
                partners = JSON.parse(currentList);
            } else if (Array.isArray(currentList)) {
                partners = currentList;
            }
        } catch (e) {
            partners = [];
        }

        const set = new Set();
        (partners || []).forEach(p => {
            if (typeof p === 'object' && p !== null) {
                if (Array.isArray(p.specialties)) {
                    p.specialties.forEach(s => { if (s) set.add(s.trim()); });
                } else if (p.specialty) {
                    set.add(p.specialty.trim());
                }
            }
        });
        return ["ALL", ...Array.from(set).sort()];
    }, [practicePartners, extraLocations, activeForm]);

    const normalize = (obj) =>
        Object.fromEntries(
            Object.entries(obj).map(([k, v]) => [k, typeof v === "string" && v.trim() === "" ? null : v])
        );

    const isInternal = useCallback((p) => {
        if (!p) return false;
        const activePractice = activeForm === 1 ? myPracticeName : (extraLocations[activeForm - 2]?.practiceName || "");
        if (!activePractice) return false;
        const practiceStr = typeof p === 'object' && p !== null ? p.practice : '';
        if (typeof practiceStr === 'string' && practiceStr.trim().toLowerCase() === activePractice.trim().toLowerCase()) {
            return true;
        }
        // Legacy fallback
        if (typeof p === 'object' && p !== null && (p.isSamePractice === true || p.isSamePractice === 'true')) {
            return true;
        }
        return false;
    }, [activeForm, myPracticeName, extraLocations]);

    const isTrustedPartner = useCallback((p) => {
        if (!p) return false;
        return Boolean(typeof p === 'object' && p !== null && p.isTrusted);
    }, []);

    const normalizeComparisonVal = (val) => {
        if (val === null || val === undefined) return "";
        if (Array.isArray(val)) return val.map(v => String(v || '').trim()).filter(Boolean).sort().join(", ").toLowerCase();
        return String(val || '').trim().toLowerCase();
    };

    const cleanPhoneDigits = (ph) => String(ph || '').replace(/\D/g, '');

    const getScrapedDifferences = useCallback((partner) => {
        if (!partner || typeof partner !== 'object' || !partner.latestScraped) return [];
        const scraped = partner.latestScraped;
        const diffs = [];

        // 1. Practice Name
        const isSamePracticeDoctor = isInternal(partner) || Boolean(partner.originalPractice);
        if (!isSamePracticeDoctor && scraped.practice && normalizeComparisonVal(partner.practice) !== normalizeComparisonVal(scraped.practice)) {
            diffs.push({
                key: 'practice',
                label: 'Practice Name',
                icon: '🏥',
                activeValue: partner.practice || '(Not set)',
                scrapedValue: scraped.practice
            });
        }

        // 2. Phone
        if (scraped.phone && cleanPhoneDigits(partner.phone) !== cleanPhoneDigits(scraped.phone)) {
            diffs.push({
                key: 'phone',
                label: 'Phone',
                icon: '📞',
                activeValue: partner.phone || '(Not set)',
                scrapedValue: scraped.phone
            });
        }

        // 3. Location / Address
        const activeAddrParts = [partner.address, partner.city, partner.state, partner.country].map(v => String(v || '').trim()).filter(Boolean);
        const activeAddressStr = activeAddrParts.join(', ');
        const scrapedAddrParts = [scraped.address, scraped.city, scraped.state, scraped.country].map(v => String(v || '').trim()).filter(Boolean);
        const scrapedAddressStr = scrapedAddrParts.join(', ') || scraped.address || '';

        if (scrapedAddressStr && normalizeComparisonVal(activeAddressStr) !== normalizeComparisonVal(scrapedAddressStr)) {
            diffs.push({
                key: 'address',
                label: 'Location / Address',
                icon: '📍',
                activeValue: activeAddressStr || '(Not set)',
                scrapedValue: scrapedAddressStr,
                scrapedLocation: {
                    address: scraped.address || '',
                    city: scraped.city || '',
                    state: scraped.state || '',
                    country: scraped.country || '',
                    zipCode: scraped.zipCode || ''
                }
            });
        }

        // 4. Specialties
        const activeSpecs = Array.isArray(partner.specialties) ? partner.specialties : (partner.specialties ? [partner.specialties] : []);
        const scrapedSpecs = Array.isArray(scraped.specialties) ? scraped.specialties : (scraped.specialties ? [scraped.specialties] : []);
        if (scrapedSpecs.length > 0 && normalizeComparisonVal(activeSpecs) !== normalizeComparisonVal(scrapedSpecs)) {
            diffs.push({
                key: 'specialties',
                label: 'Specialties',
                icon: '🩺',
                activeValue: activeSpecs.join(', ') || '(Not set)',
                scrapedValue: scrapedSpecs.join(', '),
                scrapedSpecialties: scrapedSpecs
            });
        }

        // 5. Distance
        if (scraped.distance && normalizeComparisonVal(partner.distance) !== normalizeComparisonVal(scraped.distance)) {
            diffs.push({
                key: 'distance',
                label: 'Distance',
                icon: '📏',
                activeValue: partner.distance || '(Not set)',
                scrapedValue: scraped.distance
            });
        }

        // 6. Bio / About
        if (scraped.bio && normalizeComparisonVal(partner.bio) !== normalizeComparisonVal(scraped.bio)) {
            diffs.push({
                key: 'bio',
                label: 'About / Bio',
                icon: '📝',
                activeValue: partner.bio || '(Not set)',
                scrapedValue: scraped.bio
            });
        }

        return diffs;
    }, [isInternal]);

    // Active location's current practice partners & synced check
    const activeLocationPartners = useMemo(() => {
        const currentList = activeForm === 1
            ? practicePartners
            : extraLocations[activeForm - 2]?.practicePartners || [];
        if (typeof currentList === "string") {
            try { return JSON.parse(currentList) || []; } catch (e) { return []; }
        }
        return Array.isArray(currentList) ? currentList : [];
    }, [practicePartners, extraLocations, activeForm]);

    const hasSyncedDoctors = activeLocationPartners.length > 0;

    const activeCounts = useMemo(() => {
        const total = activeLocationPartners.length;
        let trusted = 0;
        let samePractice = 0;
        activeLocationPartners.forEach(p => {
            if (isTrustedPartner(p)) trusted++;
            if (isInternal(p)) samePractice++;
        });
        return { total, trusted, samePractice };
    }, [activeLocationPartners, isTrustedPartner, isInternal]);

    const filteredPartners = useMemo(() => {
        const query = (doctorSearchTerm || "").trim().toLowerCase();
        const searchTokens = query
            ? query.split(/[\s,]+/).map(t => t.replace(/[.,\/#!$%\^&\*;:{}=\-_`~()]/g, "").trim()).filter(Boolean)
            : [];

        return activeLocationPartners.filter(p => {
            if (filterTab === "trusted" && !isTrustedPartner(p)) return false;
            if (filterTab === "same_practice" && !isInternal(p)) return false;
            if (filterTab === "external" && isInternal(p)) return false;

            if (selectedSpecialtyFilter !== "ALL") {
                const pSpecs = typeof p === 'object' && p !== null ? (Array.isArray(p.specialties) ? p.specialties : [p.specialty]) : [];
                if (!pSpecs.includes(selectedSpecialtyFilter)) return false;
            }

            if (searchTokens.length === 0) return true;

            const isObj = typeof p === 'object' && p !== null;
            const name = isObj ? (p.name || '') : String(p || '');
            const nickname = isObj ? (p.nickname || '') : '';
            const spec = isObj ? (Array.isArray(p.specialties) ? p.specialties.join(' ') : (p.specialties || p.specialty || '')) : '';
            const prac = isObj ? (p.practice || '') : '';
            const loc = isObj ? [p.address, p.city, p.state, p.country].filter(Boolean).join(' ') : '';

            const combinedRaw = `${name} ${nickname} ${spec} ${prac} ${loc}`.toLowerCase();
            const combinedNormalized = combinedRaw.replace(/[.,\/#!$%\^&\*;:{}=\-_`~()]/g, " ");

            // LIKE search: all search tokens must match anywhere in the provider details (order-independent)
            return searchTokens.every(token =>
                combinedRaw.includes(token) || combinedNormalized.includes(token)
            );
        });
    }, [activeLocationPartners, doctorSearchTerm, selectedSpecialtyFilter, filterTab, isInternal, isTrustedPartner]);

    useEffect(() => {
        setVisibleCount(20);
    }, [doctorSearchTerm, selectedSpecialtyFilter, filterTab]);

    const trustedPartnersList = useMemo(
        () => filteredPartners.filter(isTrustedPartner),
        [filteredPartners, isTrustedPartner]
    );
    const samePracticePartnersList = useMemo(
        () => filteredPartners.filter(p => isInternal(p) && !isTrustedPartner(p)),
        [filteredPartners, isInternal, isTrustedPartner]
    );
    const externalPartnersList = useMemo(
        () => filteredPartners.filter(p => !isInternal(p) && !isTrustedPartner(p)),
        [filteredPartners, isInternal, isTrustedPartner]
    );

    const visibleExternalPartners = useMemo(
        () => externalPartnersList.slice(0, visibleCount),
        [externalPartnersList, visibleCount]
    );

    // Infinite scroll: observe a sentinel element at the bottom for external providers
    const loadMoreRef = useRef(null);
    const listContainerRef = useRef(null);

    useEffect(() => {
        if (!loadMoreRef.current) return;
        const observer = new IntersectionObserver(
            (entries) => {
                if (entries[0].isIntersecting && visibleCount < externalPartnersList.length) {
                    setVisibleCount(prev => prev + 20);
                }
            },
            { root: listContainerRef.current, threshold: 0.1 }
        );
        observer.observe(loadMoreRef.current);
        return () => observer.disconnect();
    }, [visibleCount, externalPartnersList.length]);

    const handleListScroll = useCallback((e) => {
        const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
        if (scrollHeight - scrollTop - clientHeight < 250 && visibleCount < externalPartnersList.length) {
            setVisibleCount(prev => prev + 20);
        }
    }, [visibleCount, externalPartnersList.length]);

    const updateProfile = async (partial) => {
        try {
            await updateProfileApi(normalize(partial));
        } catch (e) {
            console.error("Profile update failed:", e?.response?.data || e.message);
        }
    };

    // Practice names derived specifically and exclusively from the active location
    const locationPracticeOptions = useMemo(() => {
        const seen = new Set();
        const list = [];

        const addName = (raw) => {
            if (!raw || typeof raw !== "string") return;
            const trimmed = raw.trim();
            if (trimmed && trimmed.toLowerCase() !== "other" && !seen.has(trimmed.toLowerCase())) {
                seen.add(trimmed.toLowerCase());
                list.push({ label: trimmed, value: trimmed });
            }
        };

        // 1. Strictly gather practice names from this active location's doctors only
        (activeLocationPartners || []).forEach(p => {
            if (p && typeof p === "object") {
                addName(p.practice);
                addName(p.practiceName);
                addName(p.originalPractice);
                addName(p.organization);
                addName(p.clinic);
                if (p.latestScraped && typeof p.latestScraped === 'object') {
                    addName(p.latestScraped.practice);
                    addName(p.latestScraped.practiceName);
                    addName(p.latestScraped.originalPractice);
                    addName(p.latestScraped.organization);
                    addName(p.latestScraped.clinic);
                }
            }
        });

        // 2. Include this active location's currently assigned practice name if set
        const currentLocPracticeName = (activeForm === 1 ? myPracticeName : extraLocations[activeForm - 2]?.practiceName) || "";
        addName(currentLocPracticeName);

        // 3. Sort alphabetically
        list.sort((a, b) => a.label.localeCompare(b.label));

        return list;
    }, [activeLocationPartners, activeForm, myPracticeName, extraLocations]);

    const loadProfileData = async () => {
        try {
            const res = await getMyProfile({ includePartners: true });
            const profile = res?.data?.profile || res?.data?.body?.data?.profile || res?.data;

            if (profile) {
                setMyPracticeName((profile.practiceName ?? "").trim());
                // Form 1 Data
                let partners = profile.practicePartners;
                if (typeof partners === 'string') {
                    try { partners = JSON.parse(partners); } catch { partners = []; }
                }
                if (!Array.isArray(partners)) partners = [];
                setPracticePartners(partners);

                setAddress((profile.address ?? "").trim());
                setCity((profile.city ?? "").trim());
                setState((profile.state ?? "").trim());
                setZipCode((profile.zipCode ?? "").trim());
                setCountry((profile.country ?? "").trim());

                if (profile.country) {
                    const c = allCountries.find(x => x.name === profile.country || x.isoCode === profile.country);
                    if (c) {
                        setSelectedCountry(c);
                        if (profile.state) {
                            const sList = State.getStatesOfCountry(c.isoCode);
                            const s = sList.find(x => x.name === profile.state || x.isoCode === profile.state);
                            if (s) setSelectedState(s);
                        }
                    }
                }

                // Extra Locations (Location 2, 3, 4, ...) - all from extraLocations
                let rawExtra = profile.extraLocations;
                if (typeof rawExtra === 'string') {
                    try { rawExtra = JSON.parse(rawExtra); } catch { rawExtra = []; }
                }
                let extraArr = (Array.isArray(rawExtra) ? rawExtra : []).slice(0, 5);

                // Hydrate dropdown selection state for each location
                const fullyFormedExtra = extraArr.map(loc => {
                    let sc = null, ss = null;
                    const cTarget = loc.country || null;
                    if (cTarget) {
                        const c = allCountries.find(x => x.name === cTarget || x.isoCode === cTarget || x.value === cTarget);
                        if (c) {
                            sc = c;
                            if (loc.state) {
                                const sList = State.getStatesOfCountry(c.isoCode);
                                const s = sList.find(x => x.name === loc.state || x.isoCode === loc.state);
                                if (s) ss = s;
                            }
                        }
                    }
                    return { ...loc, country: loc.country || "", practiceName: loc.practiceName || "", _selectedCountry: sc, _selectedState: ss };
                });

                setExtraLocations(fullyFormedExtra);
            }
        } catch (e) {
            console.error("Load profile error:", e?.response?.data || e.message);
        } finally {
            setLoaded(true);
        }
    };

    useEffect(() => {
        loadProfileData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);


    const handleGrabLocation = async () => {
        if (!navigator.geolocation) {
            toast.error("Geolocation is not supported by your browser");
            return;
        }

        const toastId = toast.loading("Fetching location...");
        navigator.geolocation.getCurrentPosition(async (position) => {
            const { latitude, longitude } = position.coords;
            try {
                const response = await fetch(`https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}&zoom=18&accept-language=en`);
                if (!response.ok) throw new Error("Location fetch failed");

                const data = await response.json();
                const addr = data.address;

                if (addr) {
                    const newCountry = addr.country;
                    const newState = addr.state;
                    const newCity = addr.city || addr.town || addr.village || addr.municipality || addr.county || "";
                    const newZip = addr.postcode || "";
                    const road = addr.road || addr.pedestrian || addr.street || "";
                    const house_number = addr.house_number || "";
                    const area = addr.suburb || addr.neighbourhood || addr.quarter || addr.residential || "";

                    let newAddress = `${house_number} ${road}`.trim();
                    if (!newAddress && area) {
                        newAddress = area;
                    } else if (newAddress && area) {
                        newAddress = `${newAddress}, ${area}`;
                    }

                    // Update state
                    const saveToExtra = (updater) => {
                        setExtraLocations(prev => {
                            const n = [...prev];
                            const idx = activeForm - 2;
                            if (!n[idx]) n[idx] = {};
                            updater(n, idx);
                            updateProfile({ extraLocations: JSON.stringify(n.map(({ _selectedCountry, _selectedState, ...rest }) => rest)) });
                            return n;
                        });
                    };

                    if (activeForm === 1) {
                        setCountry(newCountry); setState(newState); setCity(newCity); setZipCode(newZip); setAddress(newAddress);
                        const c = allCountries.find(x => x.name === newCountry || x.isoCode === newCountry);
                        if (c) { setSelectedCountry(c); const sl = State.getStatesOfCountry(c.isoCode); const s = sl.find(x => x.name === newState || x.isoCode === newState); if (s) setSelectedState({ label: s.name, value: s.isoCode, ...s }); }
                        await updateProfile({ country: newCountry, state: newState, city: newCity, zipCode: newZip, address: newAddress });
                    } else {
                        saveToExtra((n, idx) => {
                            let sc = null, ss = null;
                            const c = allCountries.find(x => x.name === newCountry || x.isoCode === newCountry);
                            if (c) { sc = c; const sl = State.getStatesOfCountry(c.isoCode); const s = sl.find(x => x.name === newState || x.isoCode === newState); if (s) ss = s; }
                            n[idx] = { ...n[idx], country: newCountry, state: newState, city: newCity, zipCode: newZip, address: newAddress, _selectedCountry: sc, _selectedState: ss };
                        });
                    }
                    toast.success("Location updated", { id: toastId });
                } else {
                    toast.error("Could not determine address", { id: toastId });
                }
            } catch (error) {
                console.error(error);
                toast.error("Failed to get location details", { id: toastId });
            }
        }, (error) => {
            console.error(error);
            toast.error("Geolocation error: " + error.message, { id: toastId });
        }, {
            enableHighAccuracy: true,
            timeout: 10000,
            maximumAge: 0
        });
    };

    // Helper: persist extraLocations to backend
    const saveExtraLocations = (updated) => {
        updateProfile({ extraLocations: JSON.stringify(updated.map(({ _selectedCountry, _selectedState, ...rest }) => rest)) });
    };

    const getActiveList = () => activeForm === 1 ? practicePartners : extraLocations[activeForm - 2]?.practicePartners || [];
    const setActiveList = (list, extraSnapshot) => {
        if (activeForm === 1) {
            setPracticePartners(list);
            updateProfile({ practicePartners: JSON.stringify(list) });
        } else {
            const n = extraSnapshot ? [...extraSnapshot] : [...extraLocations];
            if (!n[activeForm - 2]) n[activeForm - 2] = {};
            n[activeForm - 2] = { ...n[activeForm - 2], practicePartners: list };
            setExtraLocations(n);
            saveExtraLocations(n);
        }
    };

    const handleDeletePartner = async (partnerToDelete) => {
        setPartnerToDelete(partnerToDelete);
    };

    const confirmDeletePartner = async () => {
        if (!partnerToDelete) return;
        let currentList = [...getActiveList()];
        let idx = currentList.findIndex(p => p === partnerToDelete);
        if (idx === -1 && typeof partnerToDelete === 'object') {
            idx = currentList.findIndex(p => JSON.stringify(p) === JSON.stringify(partnerToDelete));
        }
        if (idx === -1) { toast.error("Could not find partner to delete"); return; }
        currentList.splice(idx, 1);
        try {
            setActiveList(currentList);
            toast.success("Partner removed");
        } catch (err) {
            console.error(err);
            toast.error("Failed to remove partner");
        }
        setPartnerToDelete(null);
    };

    const handleDeleteAllPartners = async () => {
        setIsDeleteAllOpen(true);
    };

    const confirmDeleteAllPartners = async () => {
        try {
            setActiveList([]);
            toast.success("All partners removed");
        } catch (err) {
            console.error(err);
            toast.error("Failed to remove partners");
        }
        setIsDeleteAllOpen(false);
    };

    const handleToggleTrusted = (partner) => {
        let currentList = [...getActiveList()];
        let idx = currentList.findIndex(p => p === partner);
        if (idx === -1 && typeof partner === 'object') {
            idx = currentList.findIndex(p => JSON.stringify(p) === JSON.stringify(partner));
        }
        if (idx === -1) return;

        const target = currentList[idx];

        const isObj = typeof target === 'object' && target !== null;
        const currentTrusted = isObj ? Boolean(target.isTrusted) : false;
        const newTrusted = !currentTrusted;

        currentList[idx] = isObj
            ? { ...target, isTrusted: newTrusted }
            : { name: target, isTrusted: newTrusted };

        try {
            setActiveList(currentList);
            toast.success(newTrusted ? "Added to Trusted Partners" : "Removed from Trusted Partners");
        } catch (err) {
            console.error(err);
            toast.error("Failed to update trusted status");
        }
    };

    const handleToggleSamePractice = (partner) => {
        let currentList = [...getActiveList()];
        let idx = currentList.findIndex(p => p === partner);
        if (idx === -1 && typeof partner === 'object') {
            idx = currentList.findIndex(p => JSON.stringify(p) === JSON.stringify(partner));
        }
        if (idx === -1) return;

        const target = currentList[idx];
        const isObj = typeof target === 'object' && target !== null;
        const currentSamePractice = isInternal(target);
        const newSamePractice = !currentSamePractice;

        const activePracticeName = activeForm === 1 ? myPracticeName : (extraLocations[activeForm - 2]?.practiceName || "");

        if (newSamePractice) {
            // Enabling Same Practice: backup previous practice name and update practice to active practice
            const previousPractice = isObj
                ? (target.originalPractice !== undefined ? target.originalPractice : (target.practice || ""))
                : "";
            const { isSamePractice: _unused, ...targetClean } = isObj ? target : {};
            currentList[idx] = isObj
                ? {
                    ...targetClean,
                    isManuallyEdited: true,
                    originalPractice: previousPractice,
                    practice: activePracticeName || target.practice || ""
                }
                : {
                    name: target,
                    isManuallyEdited: true,
                    originalPractice: "",
                    practice: activePracticeName || ""
                };
        } else {
            // Disabling Same Practice: restore the backed up original practice
            const restoredPractice = isObj
                ? (target.originalPractice !== undefined ? target.originalPractice : (target.practice === activePracticeName ? "" : (target.practice || "")))
                : "";
            const { originalPractice, isSamePractice: _unused, ...rest } = isObj ? target : { name: target };
            currentList[idx] = {
                ...rest,
                practice: restoredPractice
            };
        }

        try {
            setActiveList(currentList);
            toast.success(newSamePractice ? "Added to Same Practice" : "Removed from Same Practice");
        } catch (err) {
            console.error(err);
            toast.error("Failed to update Same Practice status");
        }
    };





    const handleApplyScrapedField = (partner, diff) => {
        let currentList = [...getActiveList()];
        let idx = currentList.findIndex(p => p === partner);
        if (idx === -1 && typeof partner === 'object') {
            idx = currentList.findIndex(p => JSON.stringify(p) === JSON.stringify(partner));
        }
        if (idx === -1) return;

        const target = { ...currentList[idx] };

        if (diff.key === 'practice') {
            target.practice = diff.scrapedValue;
            delete target.originalPractice;
            delete target.isSamePractice;
        } else if (diff.key === 'phone') {
            target.phone = diff.scrapedValue;
        } else if (diff.key === 'address' && diff.scrapedLocation) {
            target.address = diff.scrapedLocation.address;
            target.city = diff.scrapedLocation.city;
            target.state = diff.scrapedLocation.state;
            target.country = diff.scrapedLocation.country;
            if (diff.scrapedLocation.zipCode) target.zipCode = diff.scrapedLocation.zipCode;
        } else if (diff.key === 'specialties') {
            target.specialties = diff.scrapedSpecialties;
        } else if (diff.key === 'distance') {
            target.distance = diff.scrapedValue;
        } else if (diff.key === 'bio') {
            target.bio = diff.scrapedValue;
        }

        currentList[idx] = target;
        try {
            setActiveList(currentList);
            toast.success(`Applied ${diff.label} from directory`);
        } catch (err) {
            console.error(err);
            toast.error(`Failed to update ${diff.label}`);
        }
    };

    const handleApplyAllScraped = (partner) => {
        if (!partner || !partner.latestScraped) return;
        let currentList = [...getActiveList()];
        let idx = currentList.findIndex(p => p === partner);
        if (idx === -1 && typeof partner === 'object') {
            idx = currentList.findIndex(p => JSON.stringify(p) === JSON.stringify(partner));
        }
        if (idx === -1) return;

        const scraped = partner.latestScraped;
        const isSamePracticeDoctor = isInternal(currentList[idx]) || Boolean(currentList[idx].originalPractice);
        const { isSamePractice: _unused, ...existingClean } = currentList[idx];
        const target = {
            ...existingClean,
            name: scraped.name || currentList[idx].name,
            practice: isSamePracticeDoctor ? currentList[idx].practice : (scraped.practice || currentList[idx].practice),
            phone: scraped.phone || currentList[idx].phone,
            address: scraped.address || currentList[idx].address,
            city: scraped.city || currentList[idx].city,
            state: scraped.state || currentList[idx].state,
            country: scraped.country || currentList[idx].country,
            zipCode: scraped.zipCode || currentList[idx].zipCode,
            specialties: scraped.specialties || currentList[idx].specialties,
            distance: scraped.distance || currentList[idx].distance,
            languages: scraped.languages || currentList[idx].languages,
            expertiseCount: scraped.expertiseCount || currentList[idx].expertiseCount,
            bio: scraped.bio || currentList[idx].bio,
            acceptingNewPatients: scraped.acceptingNewPatients ?? currentList[idx].acceptingNewPatients,
            // Keep custom nickname, trusted status, and preserve latestScraped
            nickname: currentList[idx].nickname,
            isTrusted: currentList[idx].isTrusted,
            latestScraped: scraped
        };

        currentList[idx] = target;
        try {
            setActiveList(currentList);
            toast.success(`Applied all directory updates for ${target.name}`);
        } catch (err) {
            console.error(err);
            toast.error("Failed to apply directory updates");
        }
    };

    const handleManualAddSubmit = async () => {
        if (!newProvider.name) return toast.error("Name is required");
        const currentCountry = activeForm === 1 ? country : extraLocations[activeForm - 2]?.country;
        const { isSamePractice: _unused, ...newProviderClean } = newProvider;
        const providerToAdd = {
            ...newProviderClean,
            country: newProvider.country || currentCountry,
            isManuallyEdited: true,
            originalPractice: editingProviderOriginal?.originalPractice,
            latestScraped: editingProviderOriginal?.latestScraped,
            specialties: Array.isArray(newProvider.specialties)
                ? newProvider.specialties
                : (typeof newProvider.specialties === 'string' && newProvider.specialties.includes(',')
                    ? newProvider.specialties.split(',').map(s => s.trim())
                    : (newProvider.specialties ? [newProvider.specialties] : []))
        };

        let currentList = [...getActiveList()];
        if (editingProviderOriginal) {
            let idx = currentList.findIndex(p => p === editingProviderOriginal);
            if (idx === -1 && typeof editingProviderOriginal === 'object') {
                idx = currentList.findIndex(p => JSON.stringify(p) === JSON.stringify(editingProviderOriginal));
            }
            if (idx !== -1) {
                currentList[idx] = providerToAdd;
                toast.success("Provider updated successfully");
            } else {
                currentList.push(providerToAdd);
                toast.success("Provider added successfully");
            }
        } else {
            currentList.push(providerToAdd);
            toast.success("Provider added successfully");
        }

        try {
            setActiveList(currentList);
            setIsManualAddOpen(false);
            setEditingProviderOriginal(null);
            setNewProvider({ name: "", nickname: "", specialties: "", practice: "", address: "", city: "", state: "", country: "", distance: "", phone: "", languages: "", expertiseCount: "", acceptingNewPatients: false, isTrusted: false, bio: "" });
            setManualSelectedCountry(null);
            setManualSelectedState(null);
        } catch (err) {
            console.error(err);
            toast.error("Failed to save provider");
        }
    };
    const pollIntervalRef = useRef(null);
    const scrapeToastId = "scrape-progress-toast";


    useEffect(() => {
        const storedJobId = localStorage.getItem("scrapeJobId");
        const storedFormNum = localStorage.getItem("scrapeFormNumber");
        if (storedJobId) {
            if (storedFormNum) setActiveForm(parseInt(storedFormNum, 10));
            setSyncingDoctors(true);
            toast.loading("Resuming provider sync...", { id: scrapeToastId });
            startPollingProgress(storedJobId);
        }
        return () => {
            if (pollIntervalRef.current) {
                clearInterval(pollIntervalRef.current);
            }
            toast.dismiss(scrapeToastId);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const startPollingProgress = (jobId) => {
        if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
        let pollCount = 0;
        const MAX_POLLS = 40; // ~5.3 minutes safety timeout

        pollIntervalRef.current = setInterval(async () => {
            pollCount++;
            if (pollCount > MAX_POLLS) {
                clearInterval(pollIntervalRef.current);
                setSyncingDoctors(false);
                setScrapeJobStatus(null);
                localStorage.removeItem("scrapeJobId");
                localStorage.removeItem("scrapeFormNumber");
                toast.dismiss(scrapeToastId);
                await loadProfileData();
                toast.success("Provider directory sync completed.", { duration: 3000 });
                return;
            }

            try {
                const progressRes = await getScrapeProgress(jobId);
                const responseData = progressRes.data;
                const job = responseData.data;
                if (responseData && responseData.__meta && responseData.__meta.message === 'Progress retrieved' && job) {
                    if (job.status === 'pending') {
                        setScrapeJobStatus(job);
                        const msg = job.statusMessage || "Syncing practice partners...";
                        toast.loading(msg, { id: scrapeToastId });
                    } else if (job.status === 'completed') {
                        clearInterval(pollIntervalRef.current);
                        setSyncingDoctors(false);
                        setScrapeJobStatus(null);
                        localStorage.removeItem("scrapeJobId");

                        if (job.result && job.result.doctors && job.result.doctors.length > 0) {
                            const targetForm = parseInt(localStorage.getItem("scrapeFormNumber") || activeForm, 10);

                            if (targetForm === 1) {
                                setPracticePartners(job.result.doctors);
                            } else {
                                setExtraLocations(prev => {
                                    const n = [...prev];
                                    if (!n[targetForm - 2]) n[targetForm - 2] = {};
                                    n[targetForm - 2] = { ...n[targetForm - 2], practicePartners: job.result.doctors };
                                    return n;
                                });
                            }
                        }

                        // Authoritative profile refresh from DB
                        await loadProfileData();
                        toast.success("Practice partners synced successfully!", { id: scrapeToastId, duration: 3000 });
                        localStorage.removeItem("scrapeFormNumber");
                    } else if (job.status === 'error') {
                        clearInterval(pollIntervalRef.current);
                        setSyncingDoctors(false);
                        setScrapeJobStatus(null);
                        localStorage.removeItem("scrapeJobId");
                        localStorage.removeItem("scrapeFormNumber");
                        toast.dismiss(scrapeToastId);

                        await loadProfileData();
                        toast.error("Failed to sync practice partners.", { duration: 3000 });
                    }
                }
            } catch (pollErr) {
                const status = pollErr.response?.status;
                // Stop polling on any definitive server error (job gone, forbidden, or server crash)
                if (status === 404 || status === 403 || status === 500) {
                    clearInterval(pollIntervalRef.current);
                    setSyncingDoctors(false);
                    setScrapeJobStatus(null);
                    localStorage.removeItem("scrapeJobId");
                    localStorage.removeItem("scrapeFormNumber");
                    toast.dismiss(scrapeToastId);

                    // Reload local profile state to pull partially synchronized doctors from DB
                    await loadProfileData();

                    if (status === 403) console.warn('[scrapeProgress] Stale job belongs to another user — cleared.');
                    if (status === 500) console.warn('[scrapeProgress] Server error on progress check — cleared stale job.');
                    if (status === 404) console.warn('[scrapeProgress] Job not found or expired — cleared.');
                } else {
                    console.error("Polling error:", pollErr);
                }
            }
        }, 8000);
    };

    const syncDoctors = async () => {
        const loc = activeForm === 1 ? { zipCode, address, city, state, country } : extraLocations[activeForm - 2] || {};

        if (!loc.address?.trim() || !loc.city?.trim() || !loc.state?.trim() || !loc.zipCode?.trim() || !loc.country?.trim()) {
            return toast.error("Please ensure the address is fully configured (Street Address, City, State, Zip Code, and Country) before syncing.");
        }

        setIsSyncConfirmOpen(false);

        // Prioritize current location's configured country over primary default country
        const effectiveCountry = loc.country || country || 'United States';

        try {
            setSyncingDoctors(true);
            const res = await syncDoctorsApi({
                address: loc.address,
                city: loc.city,
                zipCode: loc.zipCode,
                state: loc.state,
                country: effectiveCountry,
                formNumber: activeForm
            });
            if (res.data.success && res.data.jobId) {
                localStorage.setItem("scrapeJobId", res.data.jobId);
                localStorage.setItem("scrapeFormNumber", activeForm);
                toast.loading("Syncing practice partners...", { id: scrapeToastId });
                startPollingProgress(res.data.jobId);
            } else if (res.data.success && res.data.doctors) {
                setActiveList(res.data.doctors);
                toast.success("Practice partners synced successfully!", { id: scrapeToastId });
                setSyncingDoctors(false);
            } else {
                toast.error(res.data.message || "Failed to sync practice partners.", { id: scrapeToastId });
                setSyncingDoctors(false);
            }
        } catch (e) {
            console.error("Sync failed:", e);
            toast.error("Failed to sync practice partners.", { id: scrapeToastId });
            setSyncingDoctors(false);
        }
    };


    return (
        <Card className="w-full max-w-full sm:max-w-[96%] xl:max-w-[1600px] 2xl:max-w-[1750px] mx-auto border sm:border-2 border-gray-200 bg-white">
            <CardHeader className="px-4 pt-4 pb-1 sm:px-6 sm:pt-6 sm:pb-2">
                <CardTitle className="text-xl sm:text-2xl font-bold">Providers Directory</CardTitle>
                <CardDescription className="text-xs sm:text-sm">
                    Manage the list of doctors and providers you collaborate with.
                </CardDescription>
            </CardHeader>
            <CardContent className="px-3 pt-1 pb-4 sm:px-6 sm:pt-2 sm:pb-6 space-y-3 sm:space-y-4">
                <div className="space-y-3 sm:space-y-4">
                    {/* Form Switcher */}
                    <div className="flex items-center gap-1.5 p-1 bg-slate-100 rounded-xl max-w-full overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
                        <Button
                            variant={activeForm === 1 ? "default" : "ghost"}
                            size="sm"
                            className={`shrink-0 rounded-lg px-3 sm:px-4 text-xs sm:text-sm ${activeForm === 1 ? "shadow-sm" : "text-slate-600"}`}
                            onClick={() => setActiveForm(1)}
                            title="Default"
                        >
                            Default
                        </Button>
                        {/* All dynamic locations rendered the same way — extraLocations[0]=Location2, [1]=Location3 … */}
                        {extraLocations.map((loc, i) => {
                            const fm = 2 + i;  // extraLocations[0] = Location 2, [1] = Location 3, etc.
                            return (
                                <div key={i} className="flex items-center shrink-0">
                                    <Button
                                        variant={activeForm === fm ? "default" : "ghost"}
                                        size="sm"
                                        className={`shrink-0 rounded-lg px-2.5 sm:px-3 text-xs sm:text-sm ${activeForm === fm ? "shadow-sm rounded-r-none" : "text-slate-600 rounded-r-none"}`}
                                        onClick={() => setActiveForm(fm)}
                                        title={`Location ${fm}`}
                                    >
                                        Location {fm}
                                    </Button>
                                    <Button
                                        variant={activeForm === fm ? "default" : "ghost"}
                                        size="icon"
                                        className={`h-8 w-6 border-l-0 rounded-l-none shrink-0 ${activeForm === fm ? "shadow-sm text-red-500 hover:bg-red-50 hover:text-red-600 border-l border-gray-200" : "text-slate-400 hover:text-red-500 hover:bg-red-50"}`}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setLocationToDelete({ index: i, formNumber: fm });
                                        }}
                                    >
                                        <X className="h-3 w-3" />
                                    </Button>
                                </div>
                            );
                        })}

                        {extraLocations.length < 5 ? (
                            <Button
                                variant="ghost"
                                size="sm"
                                className="shrink-0 rounded-lg px-2 text-xs sm:text-sm text-slate-600 hover:text-blue-600 hover:bg-blue-50"
                                onClick={() => {
                                    if (extraLocations.length >= 5) {
                                        toast.error("Maximum 5 extra locations allowed");
                                        return;
                                    }
                                    setExtraLocations(prev => {
                                        if (prev.length >= 5) return prev;
                                        const n = [...prev, {}];
                                        setActiveForm(n.length + 1); // new tab = extraLocations.length + 1
                                        return n;
                                    });
                                }}
                            >
                                <Plus className="h-4 w-4 mr-1" /> Add
                            </Button>
                        ) : (
                            <span className="shrink-0 text-xs text-slate-400 px-2 italic font-medium select-none" title="Maximum 5 extra locations allowed">
                                (Max 5 extra locations)
                            </span>
                        )}
                    </div>

                    {/* Location Inputs — unified: activeForm 1 = Default cols, activeForm >= 2 = extraLocations[activeForm-2] */}
                    {(() => {
                        const idx = activeForm - 2;  // extraLocations index for current tab
                        const isDefault = activeForm === 1;
                        const loc = isDefault ? null : extraLocations[idx] || {};

                        const getVal = (key) => {
                            if (key === "practiceName") {
                                return isDefault ? (myPracticeName || "") : (loc?.practiceName || "");
                            }
                            return isDefault ? ({ address, city, state, zipCode, country })[key] || "" : (loc[key] || "");
                        };
                        const setVal = (key, value) => {
                            if (key === "practiceName") {
                                if (isDefault) {
                                    setMyPracticeName(value);
                                } else {
                                    setExtraLocations(prev => {
                                        const n = [...prev];
                                        if (!n[idx]) n[idx] = {};
                                        n[idx] = { ...n[idx], practiceName: value };
                                        return n;
                                    });
                                }
                                return;
                            }
                            if (isDefault) {
                                ({ address: setAddress, city: setCity, state: setState, zipCode: setZipCode, country: setCountry })[key]?.(value);
                            } else {
                                setExtraLocations(prev => {
                                    const n = [...prev];
                                    if (!n[idx]) n[idx] = {};
                                    n[idx][key] = value;
                                    return n;
                                });
                            }
                        };
                        const saveVal = (key, value) => {
                            const trimmed = typeof value === 'string' ? value.trim() : value;
                            if (key === "practiceName") {
                                if (isDefault) {
                                    setMyPracticeName(trimmed);
                                    updateProfile({ practiceName: trimmed || null });
                                } else {
                                    setExtraLocations(prev => {
                                        const n = [...prev];
                                        if (!n[idx]) n[idx] = {};
                                        n[idx] = { ...n[idx], practiceName: trimmed };
                                        saveExtraLocations(n);
                                        return n;
                                    });
                                }
                                return;
                            }
                            if (isDefault) {
                                updateProfile({ [key]: trimmed || null });
                            } else {
                                setExtraLocations(prev => {
                                    const n = [...prev];
                                    if (!n[idx]) n[idx] = {};
                                    n[idx][key] = trimmed;
                                    saveExtraLocations(n);
                                    return n;
                                });
                            }
                        };

                        const selCountry = isDefault
                            ? selectedCountry
                            : (loc._selectedCountry || (loc.country ? (allCountries.find(x => x.name === loc.country || x.isoCode === loc.country || x.value === loc.country) || { label: loc.country, value: loc.country, name: loc.country, isoCode: loc.country }) : null));
                        const selState = isDefault
                            ? selectedState
                            : (loc._selectedState || (loc.state && selCountry?.isoCode ? (State.getStatesOfCountry(selCountry.isoCode).map(s => ({ label: s.name, value: s.isoCode, ...s })).find(x => x.name === loc.state || x.value === loc.state || x.label === loc.state) || null) : null));
                        const locStates = selCountry?.isoCode ? State.getStatesOfCountry(selCountry.isoCode).map(s => ({ label: s.name, value: s.isoCode, ...s })) : [];
                        const locCities = (selCountry?.isoCode && selState?.isoCode) ? City.getCitiesOfState(selCountry.isoCode, selState.isoCode).map(c => ({ label: c.name, value: c.name, ...c })) : [];

                        const currentAddressSummary = [getVal('address'), getVal('city'), getVal('state'), getVal('zipCode'), getVal('country')].filter(Boolean).join(", ");
                        const currentPracticeName = getVal('practiceName');

                        return (
                            <div className="rounded-xl border border-slate-200 bg-white p-3.5 sm:p-4 shadow-2xs space-y-3 sm:space-y-4">
                                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                                    <div className="flex items-start sm:items-center gap-2.5 sm:gap-3">
                                        <div className="h-9 w-9 sm:h-10 sm:w-10 rounded-xl bg-blue-50 border border-blue-100 flex items-center justify-center text-blue-600 shrink-0 mt-0.5 sm:mt-0">
                                            <MapPin className="h-4.5 w-4.5 sm:h-5 sm:w-5" />
                                        </div>
                                        <div className="min-w-0">
                                            <div className="flex items-center gap-1.5 sm:gap-2 flex-wrap">
                                                <span className="font-semibold text-slate-900 text-xs sm:text-sm">
                                                    {currentPracticeName || (isDefault ? "Primary Practice Location" : `Location ${activeForm}`)}
                                                </span>
                                                <span className="text-xs font-semibold bg-slate-100 text-slate-600 px-2 py-0.5 rounded-full border border-slate-200">
                                                    {isDefault ? "Default" : `Location ${activeForm}`}
                                                </span>
                                                {!hasSyncedDoctors && (
                                                    <span className="text-xs font-medium text-amber-700 bg-amber-50 border border-amber-200 px-1.5 py-0.5 rounded-md">
                                                        Sync required
                                                    </span>
                                                )}
                                            </div>
                                            <p className="text-xs text-slate-500 mt-0.5 truncate max-w-2xl">
                                                {currentAddressSummary || (
                                                    <span className="text-amber-600 font-medium">⚠️ Address not configured yet. Click "Configure Address" to set up.</span>
                                                )}
                                            </p>
                                        </div>
                                    </div>

                                    <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={() => setIsLocationDetailsOpen(prev => !prev)}
                                        className="h-8 text-xs font-medium text-slate-700 shrink-0 gap-1.5 border-slate-200 hover:bg-slate-50 w-full sm:w-auto justify-center"
                                    >
                                        <MapPin className="h-3.5 w-3.5 text-slate-500" />
                                        {isLocationDetailsOpen ? "Hide Address Details" : "Configure Address"}
                                        <ChevronDown className={`h-3.5 w-3.5 transition-transform duration-200 ${isLocationDetailsOpen ? "rotate-180" : ""}`} />
                                    </Button>
                                </div>

                                {isLocationDetailsOpen && (
                                    <div className="pt-4 border-t border-slate-100 animate-in fade-in duration-200">
                                        {(!loaded || isFormTransitioning) ? (
                                            /* Skeleton loader for location fields */
                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 animate-pulse">
                                                {/* Address — full width */}
                                                <div className="col-span-full space-y-2">
                                                    <div className="h-3.5 w-16 rounded bg-slate-200" />
                                                    <div className="h-11 w-full rounded-xl bg-slate-100" />
                                                </div>
                                                {/* Country */}
                                                <div className="space-y-2">
                                                    <div className="h-3.5 w-14 rounded bg-slate-200" />
                                                    <div className="h-11 w-full rounded-xl bg-slate-100" />
                                                </div>
                                                {/* State */}
                                                <div className="space-y-2">
                                                    <div className="h-3.5 w-10 rounded bg-slate-200" />
                                                    <div className="h-11 w-full rounded-xl bg-slate-100" />
                                                </div>
                                                {/* City */}
                                                <div className="space-y-2">
                                                    <div className="h-3.5 w-8 rounded bg-slate-200" />
                                                    <div className="h-11 w-full rounded-xl bg-slate-100" />
                                                </div>
                                                {/* Zip Code */}
                                                <div className="space-y-2">
                                                    <div className="h-3.5 w-16 rounded bg-slate-200" />
                                                    <div className="h-11 w-full rounded-xl bg-slate-100" />
                                                </div>
                                            </div>
                                        ) : (
                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                                <div className="col-span-full">
                                                    <div className="flex items-center justify-between">
                                                        <Label htmlFor="locationPracticeName">
                                                            Your Practice Name {isDefault ? "(Default Location)" : `(Location ${activeForm})`}
                                                        </Label>
                                                    </div>
                                                    <SearchableCombobox
                                                        options={locationPracticeOptions}
                                                        value={getVal('practiceName')}
                                                        onChange={(v) => setVal('practiceName', v)}
                                                        onBlur={(v) => saveVal('practiceName', (v || "").trim())}
                                                        placeholder="e.g., Lakeside Family Medicine"
                                                        itemLabel="practice"
                                                    />
                                                </div>

                                                <div className="col-span-full">
                                                    <div className="flex items-center justify-between">
                                                        <Label>Address</Label>
                                                        <Button variant="ghost" size="sm" className="h-6 text-xs text-blue-600" onClick={handleGrabLocation}>
                                                            <MapPin className="mr-1 h-3 w-3" /> Grab Current Location
                                                        </Button>
                                                    </div>
                                                    <Input
                                                        value={getVal('address')}
                                                        onChange={(e) => setVal('address', e.target.value)}
                                                        onBlur={(e) => saveVal('address', e.target.value.trim())}
                                                        placeholder="123 Medical Dr"
                                                        className="mt-2"
                                                    />
                                                </div>

                                                <div>
                                                    <Label>Country</Label>
                                                    <SingleSelectDropdown
                                                        options={allCountries}
                                                        value={selCountry?.isoCode}
                                                        placeholder="Select Country"
                                                        onSelect={async (val) => {
                                                            const c = allCountries.find(x => x.value === val);
                                                            const cMapped = c ? { label: c.name, value: c.isoCode, ...c } : null;
                                                            if (isDefault) {
                                                                setSelectedCountry(cMapped); setSelectedState(null);
                                                                setCountry(c ? c.name : ""); setState(""); setCity("");
                                                                await updateProfile({ country: c ? c.name : "", state: null, city: null });
                                                            } else {
                                                                setExtraLocations(prev => {
                                                                    const n = [...prev];
                                                                    if (!n[idx]) n[idx] = {};
                                                                    n[idx] = {
                                                                        ...n[idx],
                                                                        country: c ? c.name : (val || ""),
                                                                        state: "",
                                                                        city: "",
                                                                        _selectedCountry: cMapped,
                                                                        _selectedState: null
                                                                    };
                                                                    saveExtraLocations(n);
                                                                    return n;
                                                                });
                                                            }
                                                        }}
                                                        triggerClassName="mt-2 h-11 w-full rounded-xl border border-slate-200 bg-white px-4 text-left text-sm text-slate-900 shadow-sm"
                                                        panelClassName="absolute z-50 bottom-full mb-1 w-full overflow-hidden rounded-xl border border-slate-200 bg-white text-slate-900 shadow-lg"
                                                    />
                                                </div>

                                                <div>
                                                    <Label>State</Label>
                                                    <SingleSelectDropdown
                                                        options={isDefault ? states : locStates}
                                                        disabled={!selCountry}
                                                        value={selState?.isoCode}
                                                        placeholder={selCountry ? "Select State" : "Select Country First"}
                                                        onSelect={async (val) => {
                                                            const availableStates = (isDefault ? states : locStates).length > 0 ? (isDefault ? states : locStates) : states;
                                                            const s = availableStates.find(x => x.value === val);
                                                            const sMapped = s ? { label: s.name, value: s.isoCode, ...s } : null;
                                                            if (isDefault) {
                                                                setSelectedState(sMapped); setState(s ? s.name : ""); setCity("");
                                                                await updateProfile({ state: s ? s.name : "", city: null });
                                                            } else {
                                                                setExtraLocations(prev => {
                                                                    const n = [...prev];
                                                                    if (!n[idx]) n[idx] = {};
                                                                    n[idx] = {
                                                                        ...n[idx],
                                                                        country: selCountry?.name || country,
                                                                        state: s ? s.name : (val || ""),
                                                                        city: "",
                                                                        _selectedCountry: selCountry,
                                                                        _selectedState: sMapped
                                                                    };
                                                                    saveExtraLocations(n);
                                                                    return n;
                                                                });
                                                            }
                                                        }}
                                                        triggerClassName="mt-2 h-11 w-full rounded-xl border border-slate-200 bg-white px-4 text-left text-sm text-slate-900 shadow-sm"
                                                        panelClassName="absolute z-50 bottom-full mb-1 w-full overflow-hidden rounded-xl border border-slate-200 bg-white text-slate-900 shadow-lg"
                                                    />
                                                </div>

                                                <div>
                                                    <Label htmlFor="city">City</Label>
                                                    {(isDefault ? cities : locCities).length > 0 ? (
                                                        <SingleSelectDropdown
                                                            options={isDefault ? cities : locCities}
                                                            disabled={!selState}
                                                            value={getVal('city')}
                                                            placeholder={selState ? "Select City" : "Select State First"}
                                                            onSelect={async (val) => {
                                                                if (isDefault) { setCity(val); await updateProfile({ city: val }); }
                                                                else { setExtraLocations(prev => { const n = [...prev]; if (!n[idx]) n[idx] = {}; n[idx].city = val; saveExtraLocations(n); return n; }); }
                                                            }}
                                                            triggerClassName="mt-2 h-11 w-full rounded-xl border border-slate-200 bg-white px-4 text-left text-sm text-slate-900 shadow-sm"
                                                            panelClassName="absolute z-50 bottom-full mb-1 w-full overflow-hidden rounded-xl border border-slate-200 bg-white text-slate-900 shadow-lg"
                                                        />
                                                    ) : (
                                                        <Input
                                                            id="city"
                                                            value={getVal('city')}
                                                            onChange={(e) => setVal('city', e.target.value)}
                                                            onBlur={(e) => saveVal('city', e.target.value.trim())}
                                                            placeholder="New York"
                                                            className="mt-2"
                                                        />
                                                    )}
                                                </div>

                                                <div>
                                                    <Label htmlFor="zipCode">Zip Code</Label>
                                                    <Input
                                                        id="zipCode"
                                                        value={getVal('zipCode')}
                                                        onChange={(e) => setVal('zipCode', e.target.value)}
                                                        onBlur={(e) => saveVal('zipCode', e.target.value.trim())}
                                                        placeholder="10001"
                                                        className="mt-2"
                                                    />
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        );
                    })()}

                    {syncingDoctors && (() => {
                        const isGuest = scrapeJobStatus?.guestMode;
                        const statusMsg = scrapeJobStatus?.statusMessage;
                        const bannerColor = isGuest
                            ? 'bg-amber-50/90 border-amber-300'
                            : 'bg-blue-50/80 border-blue-200';
                        const iconColor = isGuest ? 'text-amber-500' : 'text-blue-600';
                        const textColor = isGuest ? 'text-amber-900' : 'text-blue-800';
                        const headingColor = isGuest ? 'text-amber-900' : 'text-blue-900';

                        const heading = scrapeJobStatus?.totalItems
                            ? `Gathering Providers: ${scrapeJobStatus.itemsScraped} / ${scrapeJobStatus.totalItems} processed`
                            : isGuest
                                ? '⚠️ Limited Sync — Servers Busy'
                                : 'Providers Directory Sync in Progress';

                        const body = statusMsg || (isGuest
                            ? 'We were only able to access a small sample of providers this time. Your existing list is preserved. Please come back and sync again later for complete results.'
                            : 'Please be patient as our system securely searches and compiles a list of medical providers in your specified location. This process can take several minutes. You may safely navigate away and return later.');

                        return (
                            <div className={`${bannerColor} border rounded-lg p-4 flex items-start justify-between gap-4 mt-4 mb-2 animate-in fade-in duration-300`}>
                                <div className="flex items-start gap-3">
                                    <Loader2 className={`w-5 h-5 ${iconColor} animate-spin shrink-0 mt-0.5`} />
                                    <div className={`text-sm ${textColor}`}>
                                        <p className={`font-semibold ${headingColor} mb-1`}>{heading}</p>
                                        <p className="leading-relaxed">{body}</p>
                                    </div>
                                </div>
                            </div>
                        );
                    })()}



                    <Dialog open={isSyncConfirmOpen} onOpenChange={setIsSyncConfirmOpen}>
                        <DialogContent className="max-w-md">
                            <DialogHeader>
                                <DialogTitle>Sync Practice Partners</DialogTitle>
                            </DialogHeader>
                            <div className="text-sm text-slate-600 space-y-4 py-2">
                                <p>
                                    Syncing nearby doctors will update your Practice Partners based on your <strong>current location</strong>. This process may take a few minutes.
                                </p>
                                <div className="bg-blue-50 p-4 rounded-lg border border-blue-100 text-blue-800 text-xs">
                                    <ul className="list-disc pl-4 space-y-2 font-medium">
                                        <li>Existing partners are <strong>never removed</strong>.</li>
                                        <li>New providers found will be <strong>added</strong> to your current list.</li>
                                        <li>Existing providers will be <strong>updated</strong> with new information if they are found again.</li>
                                        <li>A maximum of 10,000 providers can be gathered for each location.</li>
                                    </ul>
                                </div>
                            </div>
                            <DialogFooter>
                                <Button variant="outline" onClick={() => setIsSyncConfirmOpen(false)}>Cancel</Button>
                                <Button onClick={syncDoctors}>Continue Sync</Button>
                            </DialogFooter>
                        </DialogContent>
                    </Dialog>

                    <Dialog open={!!partnerToDelete} onOpenChange={(open) => !open && setPartnerToDelete(null)}>
                        <DialogContent className="max-w-md">
                            <DialogHeader>
                                <DialogTitle>Remove Partner</DialogTitle>
                            </DialogHeader>
                            <div className="text-sm text-slate-600 py-2">
                                Are you sure you want to remove this partner? This action cannot be undone.
                            </div>
                            <DialogFooter>
                                <Button variant="outline" onClick={() => setPartnerToDelete(null)}>Cancel</Button>
                                <Button variant="destructive" onClick={confirmDeletePartner}>Delete</Button>
                            </DialogFooter>
                        </DialogContent>
                    </Dialog>

                    <Dialog open={isDeleteAllOpen} onOpenChange={setIsDeleteAllOpen}>
                        <DialogContent className="max-w-md">
                            <DialogHeader>
                                <DialogTitle>Remove All Partners</DialogTitle>
                            </DialogHeader>
                            <div className="text-sm text-slate-600 py-2">
                                Are you sure you want to remove ALL practice partners? This cannot be undone.
                            </div>
                            <DialogFooter>
                                <Button variant="outline" onClick={() => setIsDeleteAllOpen(false)}>Cancel</Button>
                                <Button variant="destructive" onClick={confirmDeleteAllPartners}>Delete All</Button>
                            </DialogFooter>
                        </DialogContent>
                    </Dialog>

                    <Dialog open={!!locationToDelete} onOpenChange={(open) => !open && setLocationToDelete(null)}>
                        <DialogContent className="max-w-md">
                            <DialogHeader>
                                <DialogTitle>Delete Location</DialogTitle>
                            </DialogHeader>
                            <div className="text-sm text-slate-600 py-2">
                                Delete Location {locationToDelete?.formNumber}? This will permanently remove its address and provider list.
                            </div>
                            <DialogFooter>
                                <Button variant="outline" onClick={() => setLocationToDelete(null)}>Cancel</Button>
                                <Button variant="destructive" onClick={() => {
                                    if (!locationToDelete) return;
                                    const { index: i, formNumber: fm } = locationToDelete;
                                    setExtraLocations(prev => {
                                        const n = [...prev];
                                        n.splice(i, 1);
                                        saveExtraLocations(n);
                                        if (activeForm === fm) setActiveForm(1);
                                        else if (activeForm > fm) setActiveForm(activeForm - 1);
                                        return n;
                                    });
                                    setLocationToDelete(null);
                                }}>Delete</Button>
                            </DialogFooter>
                        </DialogContent>
                    </Dialog>



                    <Dialog open={isManualAddOpen} onOpenChange={(open) => {
                        setIsManualAddOpen(open);
                        if (!open) {
                            setEditingProviderOriginal(null);
                            setNewProvider({ name: "", nickname: "", specialties: "", practice: "", address: "", city: "", state: "", country: "", distance: "", phone: "", languages: "", expertiseCount: "", acceptingNewPatients: false, isTrusted: false, bio: "" });
                            setManualSelectedCountry(null);
                            setManualSelectedState(null);
                        }
                    }}>

                        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                            <DialogHeader>
                                <DialogTitle>{editingProviderOriginal ? 'Edit Provider' : 'Add Manual Provider'}</DialogTitle>
                            </DialogHeader>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 py-4">

                                {(() => {
                                    const ls = editingProviderOriginal?.latestScraped;
                                    if (!ls) return null;
                                    const scrapedSpecStr = Array.isArray(ls.specialties) ? ls.specialties.join(", ") : (ls.specialties || "");
                                    const scrapedAddrStr = [ls.address, ls.city, ls.state, ls.country].map(v => String(v || '').trim()).filter(Boolean).join(", ") || ls.address || "";
                                    const currentAddrStr = [newProvider.address, newProvider.city, newProvider.state, newProvider.country].map(v => String(v || '').trim()).filter(Boolean).join(", ");

                                    const hasSpecDiff = Boolean(scrapedSpecStr && normalizeComparisonVal(newProvider.specialties) !== normalizeComparisonVal(scrapedSpecStr));
                                    const hasPracticeDiff = Boolean(ls.practice && normalizeComparisonVal(newProvider.practice) !== normalizeComparisonVal(ls.practice));
                                    const hasAddrDiff = Boolean(scrapedAddrStr && normalizeComparisonVal(currentAddrStr) !== normalizeComparisonVal(scrapedAddrStr));
                                    const hasPhoneDiff = Boolean(ls.phone && cleanPhoneDigits(newProvider.phone) !== cleanPhoneDigits(ls.phone));
                                    const hasBioDiff = Boolean(ls.bio && normalizeComparisonVal(newProvider.bio) !== normalizeComparisonVal(ls.bio));

                                    const diffCount = [hasSpecDiff, hasPracticeDiff, hasAddrDiff, hasPhoneDiff, hasBioDiff].filter(Boolean).length;

                                    return (
                                        <div className="md:col-span-2">
                                            {diffCount > 0 ? (
                                                <div className="bg-emerald-50/90 border border-emerald-300 rounded-xl p-3.5 text-xs text-emerald-950 flex flex-col sm:flex-row sm:items-center justify-between gap-3 shadow-2xs">
                                                    <div className="flex items-start sm:items-center gap-2.5">
                                                        <Globe className="h-4 w-4 text-emerald-600 shrink-0 mt-0.5 sm:mt-0" />
                                                        <div>
                                                            <p className="font-semibold text-emerald-900">
                                                                Directory data is saved for this provider ({diffCount} field{diffCount > 1 ? 's differ' : ' differs'}).
                                                            </p>
                                                            <p className="text-emerald-800 text-xs mt-0.5">
                                                                Click <strong>"Use this"</strong> below any highlighted field to copy values from the directory:
                                                            </p>
                                                        </div>
                                                    </div>
                                                    <Button
                                                        type="button"
                                                        size="sm"
                                                        className="h-7 text-xs bg-emerald-600 hover:bg-emerald-700 text-white font-medium shrink-0 shadow-xs"
                                                        onClick={() => {
                                                            setNewProvider(prev => ({
                                                                ...prev,
                                                                specialties: scrapedSpecStr || prev.specialties,
                                                                practice: ls.practice || prev.practice,
                                                                address: ls.address || prev.address,
                                                                city: ls.city || prev.city,
                                                                state: ls.state || prev.state,
                                                                country: ls.country || prev.country,
                                                                phone: ls.phone || prev.phone,
                                                                bio: ls.bio || prev.bio
                                                            }));
                                                            if (ls.country) {
                                                                const c = allCountries.find(x => x.name === ls.country || x.isoCode === ls.country);
                                                                if (c) {
                                                                    setManualSelectedCountry(c);
                                                                    if (ls.state) {
                                                                        const sl = State.getStatesOfCountry(c.isoCode);
                                                                        const s = sl.find(x => x.name === ls.state || x.isoCode === ls.state);
                                                                        if (s) setManualSelectedState(s);
                                                                    }
                                                                }
                                                            }
                                                        }}
                                                    >
                                                        Use All Directory Values
                                                    </Button>
                                                </div>
                                            ) : (
                                                <div className="bg-blue-50/70 border border-blue-200 rounded-xl p-3 text-xs text-blue-950 flex items-center gap-2.5 shadow-2xs">
                                                    <Check className="h-4 w-4 text-blue-600 shrink-0" />
                                                    <div>
                                                        <span className="font-semibold text-blue-900">All fields currently match the saved directory data.</span>
                                                        <span className="text-blue-800 ml-1">If you edit any field, a <strong>"Use this"</strong> button will automatically appear below it so you can restore the directory value anytime.</span>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    );
                                })()}

                                <div className="space-y-2">
                                    <Label>Name *</Label>
                                    <Input
                                        value={newProvider.name}
                                        onChange={(e) => setNewProvider({ ...newProvider, name: e.target.value })}
                                        placeholder="Dr. John Doe"
                                    />
                                </div>

                                <div className="space-y-2">
                                    <Label>Nickname</Label>
                                    <Input
                                        value={newProvider.nickname}
                                        onChange={(e) => setNewProvider({ ...newProvider, nickname: e.target.value })}
                                        placeholder="e.g. Dr. John"
                                    />
                                </div>

                                <div className="space-y-2">
                                    <Label>Specialties (comma separated)</Label>
                                    <Input
                                        value={newProvider.specialties}
                                        onChange={(e) => setNewProvider({ ...newProvider, specialties: e.target.value })}
                                        placeholder="Cardiology, Internal Medicine"
                                    />
                                    {(() => {
                                        const ls = editingProviderOriginal?.latestScraped;
                                        if (!ls) return null;
                                        const scrapedSpecStr = Array.isArray(ls.specialties) ? ls.specialties.join(", ") : (ls.specialties || "");
                                        if (!scrapedSpecStr) return null;
                                        const isDiff = normalizeComparisonVal(newProvider.specialties) !== normalizeComparisonVal(scrapedSpecStr);
                                        if (isDiff) {
                                            return (
                                                <button
                                                    type="button"
                                                    className="text-xs text-emerald-800 bg-emerald-50 hover:bg-emerald-100 border border-emerald-300 rounded-lg px-2.5 py-1.5 flex items-center justify-between gap-1 w-full text-left transition-colors cursor-pointer"
                                                    onClick={() => setNewProvider(prev => ({ ...prev, specialties: scrapedSpecStr }))}
                                                >
                                                    <span className="truncate">🌐 Directory: <strong>{scrapedSpecStr}</strong></span>
                                                    <span className="text-emerald-800 font-bold shrink-0 underline ml-1">Use this</span>
                                                </button>
                                            );
                                        }
                                        return (
                                            <div className="text-xs text-slate-500 bg-slate-50 border border-slate-200 rounded-lg px-2.5 py-1 flex items-center gap-1.5 w-full">
                                                <Check className="h-3 w-3 text-emerald-600 shrink-0" />
                                                <span className="truncate">Matches Directory: {scrapedSpecStr}</span>
                                            </div>
                                        );
                                    })()}
                                </div>

                                <div className="space-y-2">
                                    <Label>Practice Name</Label>
                                    <SearchableCombobox
                                        options={locationPracticeOptions}
                                        value={newProvider.practice}
                                        onChange={(v) => setNewProvider({ ...newProvider, practice: v })}
                                        placeholder="Search or type Practice Name..."
                                    />
                                    {(() => {
                                        const ls = editingProviderOriginal?.latestScraped;
                                        if (!ls?.practice) return null;
                                        const isDiff = normalizeComparisonVal(newProvider.practice) !== normalizeComparisonVal(ls.practice);
                                        if (isDiff) {
                                            return (
                                                <button
                                                    type="button"
                                                    className="text-xs text-emerald-800 bg-emerald-50 hover:bg-emerald-100 border border-emerald-300 rounded-lg px-2.5 py-1.5 flex items-center justify-between gap-1 w-full text-left transition-colors cursor-pointer"
                                                    onClick={() => setNewProvider(prev => ({ ...prev, practice: ls.practice }))}
                                                >
                                                    <span className="truncate">🌐 Directory: <strong>{ls.practice}</strong></span>
                                                    <span className="text-emerald-800 font-bold shrink-0 underline ml-1">Use this</span>
                                                </button>
                                            );
                                        }
                                        return (
                                            <div className="text-xs text-slate-500 bg-slate-50 border border-slate-200 rounded-lg px-2.5 py-1 flex items-center gap-1.5 w-full">
                                                <Check className="h-3 w-3 text-emerald-600 shrink-0" />
                                                <span className="truncate">Matches Directory: {ls.practice}</span>
                                            </div>
                                        );
                                    })()}
                                </div>

                                <div className="space-y-2 md:col-span-2">
                                    <Label>Address</Label>
                                    <Input
                                        value={newProvider.address}
                                        onChange={(e) => setNewProvider({ ...newProvider, address: e.target.value })}
                                        placeholder="123 Medical Dr"
                                    />
                                    {(() => {
                                        const ls = editingProviderOriginal?.latestScraped;
                                        if (!ls) return null;
                                        const scrapedAddrStr = [ls.address, ls.city, ls.state, ls.country].map(v => String(v || '').trim()).filter(Boolean).join(", ") || ls.address || "";
                                        if (!scrapedAddrStr) return null;
                                        const currentAddrStr = [newProvider.address, newProvider.city, newProvider.state, newProvider.country].map(v => String(v || '').trim()).filter(Boolean).join(", ");
                                        const isDiff = normalizeComparisonVal(currentAddrStr) !== normalizeComparisonVal(scrapedAddrStr);
                                        if (isDiff) {
                                            return (
                                                <button
                                                    type="button"
                                                    className="text-xs text-emerald-800 bg-emerald-50 hover:bg-emerald-100 border border-emerald-300 rounded-lg px-2.5 py-1.5 flex items-center justify-between gap-1 w-full text-left transition-colors cursor-pointer"
                                                    onClick={() => {
                                                        setNewProvider(prev => ({
                                                            ...prev,
                                                            address: ls.address || prev.address,
                                                            city: ls.city || prev.city,
                                                            state: ls.state || prev.state,
                                                            country: ls.country || prev.country
                                                        }));
                                                        if (ls.country) {
                                                            const c = allCountries.find(x => x.name === ls.country || x.isoCode === ls.country);
                                                            if (c) {
                                                                setManualSelectedCountry(c);
                                                                if (ls.state) {
                                                                    const sl = State.getStatesOfCountry(c.isoCode);
                                                                    const s = sl.find(x => x.name === ls.state || x.isoCode === ls.state);
                                                                    if (s) setManualSelectedState(s);
                                                                }
                                                            }
                                                        }
                                                    }}
                                                >
                                                    <span className="truncate">🌐 Directory Location: <strong>{scrapedAddrStr}</strong></span>
                                                    <span className="text-emerald-800 font-bold shrink-0 underline ml-1">Use this</span>
                                                </button>
                                            );
                                        }
                                        return (
                                            <div className="text-xs text-slate-500 bg-slate-50 border border-slate-200 rounded-lg px-2.5 py-1 flex items-center gap-1.5 w-full">
                                                <Check className="h-3 w-3 text-emerald-600 shrink-0" />
                                                <span className="truncate">Matches Directory: {scrapedAddrStr}</span>
                                            </div>
                                        );
                                    })()}
                                </div>

                                <div className="space-y-2">
                                    <Label>Country</Label>
                                    <SingleSelectDropdown
                                        options={allCountries}
                                        value={manualSelectedCountry ? manualSelectedCountry.isoCode : (newProvider.country === country ? allCountries.find(c => c.name === country)?.isoCode : undefined)}
                                        placeholder="Select Country"
                                        onSelect={(val) => {
                                            const c = allCountries.find(x => x.value === val);
                                            setManualSelectedCountry(c);
                                            setManualSelectedState(null);
                                            setNewProvider({ ...newProvider, country: c.name, state: "", city: "" });
                                        }}
                                        triggerClassName="h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                                        panelClassName="absolute z-50 bottom-full mb-1 w-full overflow-hidden rounded-xl border border-slate-200 bg-white text-slate-900 shadow-lg"
                                    />
                                </div>

                                <div className="space-y-2">
                                    <Label>State</Label>
                                    <SingleSelectDropdown
                                        options={manualStates}
                                        disabled={!manualSelectedCountry}
                                        value={manualSelectedState ? manualSelectedState.isoCode : undefined}
                                        placeholder={manualSelectedCountry ? "Select State" : "Select Country First"}
                                        onSelect={(val) => {
                                            const s = manualStates.find(x => x.value === val);
                                            setManualSelectedState(s);
                                            setNewProvider({ ...newProvider, state: s.name, city: "" });
                                        }}
                                        triggerClassName="h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                                        panelClassName="absolute z-50 bottom-full mb-1 w-full overflow-hidden rounded-xl border border-slate-200 bg-white text-slate-900 shadow-lg"
                                    />
                                </div>

                                <div className="space-y-2">
                                    <Label>City</Label>
                                    {manualCities.length > 0 ? (
                                        <SingleSelectDropdown
                                            options={manualCities}
                                            disabled={!manualSelectedState}
                                            value={newProvider.city}
                                            placeholder={manualSelectedState ? "Select City" : "Select State First"}
                                            onSelect={(val) => {
                                                setNewProvider({ ...newProvider, city: val });
                                            }}
                                            triggerClassName="h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                                            panelClassName="absolute z-50 bottom-full mb-1 w-full overflow-hidden rounded-xl border border-slate-200 bg-white text-slate-900 shadow-lg"
                                        />
                                    ) : (
                                        <Input
                                            value={newProvider.city}
                                            onChange={(e) => setNewProvider({ ...newProvider, city: e.target.value })}
                                            placeholder="New York"
                                        />
                                    )}
                                </div>

                                <div className="space-y-2">
                                    <Label>Distance</Label>
                                    <Input
                                        value={newProvider.distance}
                                        onChange={(e) => setNewProvider({ ...newProvider, distance: e.target.value })}
                                        placeholder="e.g. 2.5 miles"
                                    />
                                </div>

                                <div className="space-y-2">
                                    <Label>Phone</Label>
                                    <Input
                                        value={newProvider.phone}
                                        onChange={(e) => setNewProvider({ ...newProvider, phone: e.target.value })}
                                        placeholder="(555) 123-4567"
                                    />
                                    {(() => {
                                        const ls = editingProviderOriginal?.latestScraped;
                                        if (!ls?.phone) return null;
                                        const isDiff = cleanPhoneDigits(newProvider.phone) !== cleanPhoneDigits(ls.phone);
                                        if (isDiff) {
                                            return (
                                                <button
                                                    type="button"
                                                    className="text-xs text-emerald-800 bg-emerald-50 hover:bg-emerald-100 border border-emerald-300 rounded-lg px-2.5 py-1.5 flex items-center justify-between gap-1 w-full text-left transition-colors cursor-pointer"
                                                    onClick={() => setNewProvider(prev => ({ ...prev, phone: ls.phone }))}
                                                >
                                                    <span className="truncate">🌐 Directory: <strong>{ls.phone}</strong></span>
                                                    <span className="text-emerald-800 font-bold shrink-0 underline ml-1">Use this</span>
                                                </button>
                                            );
                                        }
                                        return (
                                            <div className="text-xs text-slate-500 bg-slate-50 border border-slate-200 rounded-lg px-2.5 py-1 flex items-center gap-1.5 w-full">
                                                <Check className="h-3 w-3 text-emerald-600 shrink-0" />
                                                <span className="truncate">Matches Directory: {ls.phone}</span>
                                            </div>
                                        );
                                    })()}
                                </div>

                                <div className="space-y-2">
                                    <Label>Languages</Label>
                                    <Input
                                        value={newProvider.languages}
                                        onChange={(e) => setNewProvider({ ...newProvider, languages: e.target.value })}
                                        placeholder="English, Spanish"
                                    />
                                </div>

                                <div className="space-y-2">
                                    <Label>Expertise Count</Label>
                                    <Input
                                        type="number"
                                        value={newProvider.expertiseCount}
                                        onChange={(e) => setNewProvider({ ...newProvider, expertiseCount: e.target.value })}
                                        placeholder="10"
                                    />
                                </div>

                                <div className="space-y-2 flex items-center space-x-2">
                                    <Checkbox
                                        id="acceptingNewPatients"
                                        checked={newProvider.acceptingNewPatients}
                                        onCheckedChange={(checked) => setNewProvider({ ...newProvider, acceptingNewPatients: checked })}
                                    />
                                    <Label htmlFor="acceptingNewPatients">Accepting New Patients</Label>
                                </div>

                                <div className="space-y-2 flex items-center space-x-2">
                                    <Checkbox
                                        id="isTrustedPartner"
                                        checked={newProvider.isTrusted}
                                        onCheckedChange={(checked) => setNewProvider({ ...newProvider, isTrusted: checked })}
                                    />
                                    <Label htmlFor="isTrustedPartner" className="flex items-center gap-1 font-medium text-amber-900">
                                        <Star className="h-3.5 w-3.5 fill-amber-400 text-amber-500" /> Mark as Trusted Partner
                                    </Label>
                                </div>

                                <div className="space-y-2 flex items-center space-x-2">
                                    {(() => {
                                        const activePracticeName = activeForm === 1 ? myPracticeName : (extraLocations[activeForm - 2]?.practiceName || "");
                                        const isSamePracticeSelected = Boolean(activePracticeName && newProvider.practice && newProvider.practice.trim().toLowerCase() === activePracticeName.trim().toLowerCase());
                                        return (
                                            <>
                                                <Checkbox
                                                    id="isSamePractice"
                                                    checked={isSamePracticeSelected}
                                                    onCheckedChange={(checked) => {
                                                        if (checked) {
                                                            const backup = (newProvider.practice && newProvider.practice.trim().toLowerCase() !== activePracticeName.trim().toLowerCase())
                                                                ? newProvider.practice
                                                                : (editingProviderOriginal?.originalPractice || editingProviderOriginal?.practice || "");
                                                            setNewProvider(prev => ({
                                                                ...prev,
                                                                practice: activePracticeName,
                                                                originalPractice: backup
                                                            }));
                                                        } else {
                                                            const restored = newProvider.originalPractice || editingProviderOriginal?.originalPractice || "";
                                                            setNewProvider(prev => ({
                                                                ...prev,
                                                                practice: restored,
                                                                originalPractice: ""
                                                            }));
                                                        }
                                                    }}
                                                />
                                                <Label htmlFor="isSamePractice" className="flex items-center gap-1 font-medium text-sky-900 cursor-pointer">
                                                    <Building2 className="h-3.5 w-3.5 text-sky-600" /> Mark as Same Practice Doctor
                                                </Label>
                                            </>
                                        );
                                    })()}
                                </div>

                                <div className="space-y-2 md:col-span-2">
                                    <Label>Bio</Label>
                                    <Textarea
                                        value={newProvider.bio}
                                        onChange={(e) => setNewProvider({ ...newProvider, bio: e.target.value })}
                                        placeholder="Brief biography..."
                                        rows={3}
                                    />
                                    {(() => {
                                        const ls = editingProviderOriginal?.latestScraped;
                                        if (!ls?.bio) return null;
                                        const isDiff = normalizeComparisonVal(newProvider.bio) !== normalizeComparisonVal(ls.bio);
                                        if (isDiff) {
                                            return (
                                                <button
                                                    type="button"
                                                    className="text-xs text-emerald-800 bg-emerald-50 hover:bg-emerald-100 border border-emerald-300 rounded-lg px-2.5 py-1.5 flex items-center justify-between gap-1 w-full text-left transition-colors cursor-pointer"
                                                    onClick={() => setNewProvider(prev => ({ ...prev, bio: ls.bio }))}
                                                >
                                                    <span className="truncate">🌐 Directory Bio: <strong>"{ls.bio}"</strong></span>
                                                    <span className="text-emerald-800 font-bold shrink-0 underline ml-1">Use this</span>
                                                </button>
                                            );
                                        }
                                        return (
                                            <div className="text-xs text-slate-500 bg-slate-50 border border-slate-200 rounded-lg px-2.5 py-1 flex items-center gap-1.5 w-full">
                                                <Check className="h-3 w-3 text-emerald-600 shrink-0" />
                                                <span className="truncate">Matches Directory Bio</span>
                                            </div>
                                        );
                                    })()}
                                </div>

                            </div>
                            <DialogFooter>
                                <Button variant="outline" onClick={() => setIsManualAddOpen(false)}>Cancel</Button>
                                <Button onClick={handleManualAddSubmit}>{editingProviderOriginal ? 'Save' : 'Add'}</Button>
                            </DialogFooter>
                        </DialogContent>
                    </Dialog>

                    {/* Progress Indicator */}
                    {syncingDoctors && scrapeJobStatus && scrapeJobStatus.totalItems > 0 && (
                        <div className="w-full mt-4 bg-slate-50 p-4 rounded-xl border border-slate-200 shadow-sm animate-in fade-in slide-in-from-top-2">
                            <div className="flex items-center justify-between mb-2">
                                <span className="text-sm font-medium text-slate-700">Syncing Providers...</span>
                                <span className="text-sm font-bold text-blue-600">
                                    {Math.round((scrapeJobStatus.itemsScraped / scrapeJobStatus.totalItems) * 100)}%
                                </span>
                            </div>
                            <div className="w-full bg-slate-200 rounded-full h-2 overflow-hidden">
                                <div
                                    className="bg-blue-600 h-2 transition-all duration-500 ease-out rounded-full"
                                    style={{ width: `${Math.min(100, (scrapeJobStatus.itemsScraped / scrapeJobStatus.totalItems) * 100)}%` }}
                                ></div>
                            </div>
                            <p className="text-xs text-slate-500 mt-2 text-right">
                                {scrapeJobStatus.itemsScraped} / {scrapeJobStatus.totalItems} providers loaded
                            </p>
                        </div>
                    )}

                    <div className="rounded-xl border border-slate-200 bg-slate-50/50 p-3 sm:p-5 space-y-3 sm:space-y-4">
                        {/* KPI Metric Chips */}
                        <div className="grid grid-cols-3 gap-2 sm:gap-3">
                            {/* All Providers */}
                            <button
                                type="button"
                                onClick={() => setFilterTab("all")}
                                className={`p-2.5 sm:p-3.5 rounded-xl border text-left transition-all ${filterTab === "all"
                                        ? "bg-blue-50/80 border-blue-500 text-blue-950 ring-2 ring-blue-500/20 shadow-xs"
                                        : "bg-white border-slate-200 text-slate-800 hover:border-blue-300 hover:bg-blue-50/20"
                                    }`}
                            >
                                <p className={`text-xs sm:text-xs font-semibold flex items-center gap-1 sm:gap-1.5 truncate ${filterTab === "all" ? "text-blue-700" : "text-slate-500"}`}>
                                    <Users className={`h-3 sm:h-3.5 w-3 sm:w-3.5 shrink-0 ${filterTab === "all" ? "text-blue-600" : "text-slate-400"}`} />
                                    <span className="truncate">All <span className="hidden sm:inline">Providers</span></span>
                                </p>
                                <p className={`text-xl sm:text-2xl font-bold mt-0.5 sm:mt-1 ${filterTab === "all" ? "text-blue-900" : "text-slate-900"}`}>{activeCounts.total}</p>
                            </button>

                            {/* Trusted Partners */}
                            <button
                                type="button"
                                onClick={() => setFilterTab("trusted")}
                                className={`p-2.5 sm:p-3.5 rounded-xl border text-left transition-all ${filterTab === "trusted"
                                        ? "bg-amber-50/80 border-amber-500 text-amber-950 ring-2 ring-amber-500/20 shadow-xs"
                                        : "bg-white border-slate-200 text-slate-800 hover:border-amber-300 hover:bg-amber-50/20"
                                    }`}
                            >
                                <p className={`text-xs sm:text-xs font-semibold flex items-center gap-1 sm:gap-1.5 truncate ${filterTab === "trusted" ? "text-amber-700" : "text-slate-500"}`}>
                                    <Star className={`h-3 sm:h-3.5 w-3 sm:w-3.5 shrink-0 ${filterTab === "trusted" ? "fill-amber-400 text-amber-500" : "fill-amber-300 text-amber-400"}`} />
                                    <span className="truncate">Trusted</span>
                                </p>
                                <p className={`text-xl sm:text-2xl font-bold mt-0.5 sm:mt-1 ${filterTab === "trusted" ? "text-amber-900" : "text-slate-900"}`}>{activeCounts.trusted}</p>
                            </button>

                            {/* Same Practice */}
                            <button
                                type="button"
                                onClick={() => setFilterTab("same_practice")}
                                className={`p-2.5 sm:p-3.5 rounded-xl border text-left transition-all ${filterTab === "same_practice"
                                        ? "bg-sky-50/80 border-sky-500 text-sky-950 ring-2 ring-sky-500/20 shadow-xs"
                                        : "bg-white border-slate-200 text-slate-800 hover:border-sky-300 hover:bg-sky-50/20"
                                    }`}
                            >
                                <p className={`text-xs sm:text-xs font-semibold flex items-center gap-1 sm:gap-1.5 truncate ${filterTab === "same_practice" ? "text-sky-700" : "text-slate-500"}`}>
                                    <Building2 className={`h-3 sm:h-3.5 w-3 sm:w-3.5 shrink-0 ${filterTab === "same_practice" ? "text-sky-600" : "text-sky-400"}`} />
                                    <span className="truncate">Same <span className="hidden sm:inline">Practice</span></span>
                                </p>
                                <p className={`text-xl sm:text-2xl font-bold mt-0.5 sm:mt-1 ${filterTab === "same_practice" ? "text-sky-900" : "text-slate-900"}`}>{activeCounts.samePractice}</p>
                            </button>
                        </div>

                        {/* Unified Action Bar */}
                        <div className="flex flex-col md:flex-row items-stretch md:items-center justify-between gap-2.5 sm:gap-3 bg-white p-2.5 sm:p-3 rounded-xl border border-slate-200 shadow-2xs">
                            <div className="flex flex-col sm:flex-row flex-1 items-stretch sm:items-center gap-2">
                                <div className="relative flex-1">
                                    <Search className="h-4 w-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                                    <Input
                                        placeholder="Search doctors by name, specialty, or practice..."
                                        value={doctorSearchTerm}
                                        onChange={(e) => setDoctorSearchTerm(e.target.value)}
                                        className="h-9 bg-slate-50/60 border-slate-200 text-xs pl-9 pr-8 rounded-lg focus:bg-white transition-colors"
                                    />
                                    {doctorSearchTerm && (
                                        <button
                                            type="button"
                                            onClick={() => setDoctorSearchTerm("")}
                                            className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                                        >
                                            <X className="h-4 w-4" />
                                        </button>
                                    )}
                                </div>

                                {availableSpecialties.length > 1 && (
                                    <select
                                        value={selectedSpecialtyFilter}
                                        onChange={(e) => setSelectedSpecialtyFilter(e.target.value)}
                                        className="h-9 rounded-lg border border-slate-200 bg-slate-50/60 px-3 text-xs font-medium text-slate-700 shadow-2xs focus:outline-none focus:ring-1 focus:ring-blue-500 focus:bg-white w-full sm:w-auto sm:max-w-[200px]"
                                    >
                                        <option value="ALL">All Specialties ({availableSpecialties.length - 1})</option>
                                        {availableSpecialties.filter(s => s !== "ALL").map((spec, idx) => (
                                            <option key={idx} value={spec}>{spec}</option>
                                        ))}
                                    </select>
                                )}
                            </div>

                            <div className="flex items-center gap-2 w-full md:w-auto">
                                <Button
                                    variant="outline"
                                    size="sm"
                                    className="flex-1 md:flex-initial h-9 text-xs font-medium border-slate-200 text-slate-700 hover:bg-slate-50 shadow-2xs truncate"
                                    onClick={() => {
                                        const loc = activeForm === 1
                                            ? { address, city, state, zipCode, country }
                                            : (extraLocations[activeForm - 2] || {});
                                        if (!loc.address?.trim() || !loc.city?.trim() || !loc.state?.trim() || !loc.zipCode?.trim() || !loc.country?.trim()) {
                                            return toast.error("Please ensure the address is fully configured (Street Address, City, State, Zip Code, and Country) before syncing.");
                                        }
                                        setIsSyncConfirmOpen(true);
                                    }}
                                    disabled={syncingDoctors}
                                >
                                    {syncingDoctors ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin text-blue-600" /> : <RefreshCw className="mr-1.5 h-3.5 w-3.5 text-slate-500" />}
                                    <span className="truncate">Sync Nearby Doctors</span>
                                </Button>

                                <Button
                                    size="sm"
                                    className="flex-1 md:flex-initial h-9 text-xs font-medium bg-blue-600 hover:bg-blue-700 text-white shadow-2xs whitespace-nowrap"
                                    onClick={() => {
                                        setEditingProviderOriginal(null);
                                        setNewProvider({ name: "", nickname: "", specialties: "", practice: "", address: "", city: "", state: "", country: "", distance: "", phone: "", languages: "", expertiseCount: "", acceptingNewPatients: false, isTrusted: false, bio: "" });
                                        setManualSelectedCountry(null);
                                        setManualSelectedState(null);
                                        setIsManualAddOpen(true);
                                    }}
                                >
                                    <Plus className="mr-1 h-3.5 w-3.5" /> <span className="truncate">Add Provider</span>
                                </Button>

                                <Button
                                    variant="ghost"
                                    size="sm"
                                    className="h-9 w-9 p-0 text-red-500 hover:text-red-700 hover:bg-red-50 shrink-0"
                                    onClick={handleDeleteAllPartners}
                                    disabled={syncingDoctors || activeLocationPartners.length === 0}
                                    title="Delete all providers for this location"
                                >
                                    <Trash2 className="h-3.5 w-3.5" />
                                </Button>
                            </div>
                        </div>

                        <div
                            ref={listContainerRef}
                            onScroll={handleListScroll}
                            className="space-y-6 max-h-[620px] xl:max-h-[680px] 2xl:max-h-[760px] overflow-y-auto pr-1.5 [scrollbar-width:thin]"
                            style={{ scrollbarGutter: 'stable' }}
                        >
                            {/* Skeleton loader — shown while initial profile data is loading */}
                            {!loaded ? (
                                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3.5">
                                    {Array.from({ length: 8 }).map((_, i) => (
                                        <div
                                            key={i}
                                            className="animate-pulse flex items-center gap-3 rounded-xl border border-slate-200 bg-white p-3.5"
                                        >
                                            <div className="h-8 w-8 rounded-full bg-slate-200 shrink-0" />
                                            <div className="flex-1 space-y-2 min-w-0">
                                                <div className="h-3 w-3/4 rounded bg-slate-200" />
                                                <div className="h-2.5 w-1/2 rounded bg-slate-100" />
                                            </div>
                                            <div className="h-4 w-4 rounded bg-slate-100 shrink-0" />
                                        </div>
                                    ))}
                                </div>
                            ) : isFormTransitioning ? (
                                /* Skeleton shown during heavy tab switch */
                                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3.5">
                                    {Array.from({ length: 8 }).map((_, i) => (
                                        <div
                                            key={i}
                                            className="animate-pulse flex items-center gap-3 rounded-xl border border-slate-200 bg-white p-3.5"
                                        >
                                            <div className="h-8 w-8 rounded-full bg-slate-200 shrink-0" />
                                            <div className="flex-1 space-y-2 min-w-0">
                                                <div className="h-3 w-3/4 rounded bg-slate-200" />
                                                <div className="h-2.5 w-1/2 rounded bg-slate-100" />
                                            </div>
                                            <div className="h-4 w-4 rounded bg-slate-100 shrink-0" />
                                        </div>
                                    ))}
                                </div>
                            ) : filteredPartners.length > 0 ? (
                                (() => {
                                    const trustedSection = trustedPartnersList;
                                    const samePracticeSection = samePracticePartnersList;
                                    const externalSection = visibleExternalPartners;

                                    const renderPartnerCard = (partner, uniqueKey) => {
                                        const isObj = typeof partner === 'object' && partner !== null;
                                        const name = isObj ? partner.name : partner;
                                        const isTrusted = isTrustedPartner(partner);
                                        const isInt = isInternal(partner);
                                        const diffs = isObj ? getScrapedDifferences(partner) : [];
                                        const partnerSpecialty = isObj
                                            ? (Array.isArray(partner.specialties)
                                                ? partner.specialties.filter(Boolean).join(", ")
                                                : (partner.specialties || partner.specialty || ""))
                                            : "";

                                        return (
                                            <AccordionItem
                                                key={uniqueKey}
                                                value={`item-${uniqueKey}`}
                                                className={`relative rounded-xl border transition-all hover:shadow-md overflow-hidden sm:overflow-visible ${isTrusted
                                                        ? "border-amber-200 bg-amber-50/20 hover:border-amber-400"
                                                        : isInt
                                                            ? "border-sky-200 bg-sky-50/10 hover:border-sky-300"
                                                            : "border-slate-200 bg-white hover:border-blue-300"
                                                    }`}
                                            >
                                                {/* Action Buttons Toolbar: Integrated top bar on mobile, floating ledge tab on desktop */}
                                                <div
                                                    className={`sm:absolute sm:-top-6 sm:right-3 sm:z-20 flex items-center justify-between sm:justify-start px-2.5 py-1.5 sm:px-0 sm:py-0 rounded-t-xl sm:rounded-t-md border-b sm:border-b-0 sm:border-t sm:border-l sm:border-r shadow-xs sm:divide-x overflow-hidden w-full sm:w-auto ${isTrusted
                                                            ? "border-amber-300 bg-amber-100/90 text-amber-800 sm:divide-amber-200"
                                                            : isInt
                                                                ? "border-sky-300 bg-sky-100/90 text-sky-800 sm:divide-sky-200"
                                                                : "border-slate-200 bg-slate-100/90 text-slate-600 sm:divide-slate-200"
                                                        }`}
                                                    onClick={(e) => e.stopPropagation()}
                                                >
                                                    {/* Mobile Status Label */}
                                                    <div className="sm:hidden flex items-center gap-1.5 text-xs font-semibold">
                                                        {isTrusted ? (
                                                            <span className="text-amber-800 flex items-center gap-1">
                                                                <Star className="h-3 w-3 fill-amber-400 text-amber-500" /> Trusted Partner
                                                            </span>
                                                        ) : isInt ? (
                                                            <span className="text-sky-800 flex items-center gap-1">
                                                                <Building2 className="h-3 w-3 text-sky-600" /> Same Practice
                                                            </span>
                                                        ) : (
                                                            <span className="text-slate-500 font-medium">Provider Actions</span>
                                                        )}
                                                    </div>

                                                    {/* Action Buttons Row */}
                                                    {/* Action Buttons Row */}
                                                    <div className="flex items-center divide-x sm:divide-x-0 divide-slate-200/60">
                                                        {/* Star Toggle Button */}
                                                        <button
                                                            type="button"
                                                            title={isTrusted ? "Trusted Partner (Click to unstar)" : "Click to mark as Trusted Partner"}
                                                            className={`h-7 w-7 sm:h-6 sm:w-6 flex items-center justify-center transition-colors ${isTrusted
                                                                    ? "bg-amber-200/90 text-amber-600 hover:bg-amber-300/80"
                                                                    : "text-slate-400 hover:text-amber-500 hover:bg-white"
                                                                }`}
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                handleToggleTrusted(partner);
                                                            }}
                                                        >
                                                            <Star className={`h-3.5 w-3.5 sm:h-3 sm:w-3 ${isTrusted ? "fill-amber-400 text-amber-500" : ""}`} />
                                                        </button>

                                                        {/* Same Practice Toggle Button */}
                                                        <button
                                                            type="button"
                                                            title={isInt ? "Same Practice Doctor (Click to remove)" : "Click to mark as Same Practice Doctor"}
                                                            className={`h-7 w-7 sm:h-6 sm:w-6 flex items-center justify-center transition-colors ${isInt
                                                                    ? "bg-sky-200/90 text-sky-700 hover:bg-sky-300/80"
                                                                    : "text-slate-400 hover:text-sky-600 hover:bg-white"
                                                                }`}
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                handleToggleSamePractice(partner);
                                                            }}
                                                        >
                                                            <Building2 className={`h-3.5 w-3.5 sm:h-3 sm:w-3 ${isInt ? "text-sky-600 fill-sky-200" : ""}`} />
                                                        </button>

                                                        {/* Edit Provider Details Button */}
                                                        <button
                                                            type="button"
                                                            title="Edit Provider Details"
                                                            className="h-7 w-7 sm:h-6 sm:w-6 flex items-center justify-center text-slate-400 hover:text-blue-600 hover:bg-white transition-colors"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                setEditingProviderOriginal(partner);
                                                                if (isObj) {
                                                                    setNewProvider({
                                                                        name: partner.name || "",
                                                                        nickname: partner.nickname || "",
                                                                        specialties: Array.isArray(partner.specialties) ? partner.specialties.join(', ') : (partner.specialties || ""),
                                                                        practice: partner.practice || "",
                                                                        originalPractice: partner.originalPractice || "",
                                                                        address: partner.address || "",
                                                                        city: partner.city || "",
                                                                        state: partner.state || "",
                                                                        country: partner.country || "",
                                                                        distance: partner.distance || "",
                                                                        phone: partner.phone || "",
                                                                        languages: partner.languages || "",
                                                                        expertiseCount: partner.expertiseCount || "",
                                                                        acceptingNewPatients: partner.acceptingNewPatients || false,
                                                                        isTrusted: partner.isTrusted || false,
                                                                        bio: partner.bio || ""
                                                                    });
                                                                    const c = allCountries.find(x => x.name === partner.country || x.isoCode === partner.country);
                                                                    if (c) {
                                                                        setManualSelectedCountry(c);
                                                                        const sl = State.getStatesOfCountry(c.isoCode);
                                                                        const s = sl.find(x => x.name === partner.state || x.isoCode === partner.state);
                                                                        if (s) setManualSelectedState(s);
                                                                    }
                                                                } else {
                                                                    setNewProvider({ name: partner, nickname: "", specialties: "", practice: "", address: "", city: "", state: "", country: "", distance: "", phone: "", languages: "", expertiseCount: "", acceptingNewPatients: false, isTrusted: false, bio: "" });
                                                                    setManualSelectedCountry(null);
                                                                    setManualSelectedState(null);
                                                                }
                                                                setIsManualAddOpen(true);
                                                            }}
                                                        >
                                                            <Pencil className="h-3 w-3 sm:h-2.5 sm:w-2.5" />
                                                        </button>

                                                        {/* Delete Provider Button */}
                                                        <button
                                                            type="button"
                                                            title="Remove Provider"
                                                            className="h-7 w-7 sm:h-6 sm:w-6 flex items-center justify-center text-slate-400 hover:text-red-600 hover:bg-white transition-colors"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                handleDeletePartner(partner);
                                                            }}
                                                        >
                                                            <Trash2 className="h-3.5 w-3.5 sm:h-3 sm:w-3" />
                                                        </button>
                                                    </div>
                                                </div>

                                                <div className="p-3">
                                                    <AccordionTrigger className="hover:no-underline py-0 w-full text-left cursor-pointer group/title">
                                                        <div className="flex items-start gap-2.5 min-w-0 flex-1 mr-2">
                                                            <div className={`h-8 w-8 rounded-full flex items-center justify-center font-bold text-xs shrink-0 select-none mt-0.5 ${isTrusted
                                                                    ? "bg-amber-100 text-amber-800 ring-2 ring-amber-300"
                                                                    : isInt
                                                                        ? "bg-sky-100 text-sky-800 ring-2 ring-sky-300"
                                                                        : "bg-slate-100 text-slate-700"
                                                                }`}>
                                                                {getInitials(name)}
                                                            </div>
                                                            <div className="min-w-0 flex-1">
                                                                <span className="font-semibold text-slate-900 text-xs sm:text-sm truncate block leading-snug group-hover/title:text-blue-600 transition-colors" title={name}>
                                                                    {isObj && partner.nickname ? `${partner.nickname} (${name})` : name}
                                                                </span>
                                                                <div className="flex items-center gap-1 mt-1 flex-wrap">
                                                                    {isInt && (
                                                                        <span className="text-xs font-semibold bg-sky-100 text-sky-800 border border-sky-200 px-1.5 py-0.2 rounded-full flex items-center gap-0.5 shrink-0">
                                                                            🏢 Same Practice
                                                                        </span>
                                                                    )}
                                                                    {isTrusted && (
                                                                        <span className="text-xs font-semibold bg-amber-100 text-amber-800 border border-amber-200 px-1.5 py-0.2 rounded-full flex items-center gap-0.5 shrink-0">
                                                                            ⭐ Trusted
                                                                        </span>
                                                                    )}
                                                                    {isObj && partner.isManuallyEdited && (
                                                                        <span className="text-xs font-medium bg-purple-100 text-purple-800 border border-purple-200 px-1.5 py-0.2 rounded-full flex items-center gap-0.5 shrink-0" title="Provider details have been manually customized">
                                                                            ✏️ Edited
                                                                        </span>
                                                                    )}
                                                                    {diffs.length > 0 && (
                                                                        <span className="text-xs font-semibold bg-emerald-100 text-emerald-800 border border-emerald-200 px-1.5 py-0.2 rounded-full flex items-center gap-0.5 shrink-0" title={`${diffs.length} updated field${diffs.length > 1 ? 's' : ''} available from directory sync`}>
                                                                            <Globe className="h-2.5 w-2.5 text-emerald-600 animate-pulse" />
                                                                            <span>Updates ({diffs.length})</span>
                                                                        </span>
                                                                    )}
                                                                    {isObj && partnerSpecialty && (
                                                                        <span className="text-xs font-medium text-slate-500 flex items-center gap-1 shrink-0 max-w-[200px] sm:max-w-xs truncate" title={partnerSpecialty}>
                                                                            <span>🩺</span>
                                                                            <span className="truncate">{partnerSpecialty}</span>
                                                                        </span>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </AccordionTrigger>
                                                </div>

                                                {isObj && (
                                                    <AccordionContent className="px-4 pb-4 pt-0">
                                                        <div className="space-y-3 pt-3 text-xs text-slate-600 border-t border-slate-100 mt-1 bg-slate-50/50 p-3 rounded-lg">
                                                            {diffs.length > 0 && (
                                                                <div className="p-2.5 rounded-lg bg-emerald-50 border border-emerald-200 flex flex-col sm:flex-row sm:items-center justify-between gap-2 shadow-2xs">
                                                                    <div className="flex items-center gap-2 text-emerald-900 text-xs">
                                                                        <Globe className="h-4 w-4 text-emerald-600 shrink-0" />
                                                                        <div>
                                                                            <span className="font-semibold">Directory has {diffs.length} updated detail{diffs.length > 1 ? 's' : ''} available.</span>
                                                                            {partner.latestScraped?.scrapedAt && (
                                                                                <span className="text-emerald-700 ml-1 text-xs block sm:inline">
                                                                                    (Synced {new Date(partner.latestScraped.scrapedAt).toLocaleDateString()})
                                                                                </span>
                                                                            )}
                                                                        </div>
                                                                    </div>
                                                                    <Button
                                                                        size="sm"
                                                                        className="h-7 text-xs bg-emerald-600 hover:bg-emerald-700 text-white font-medium shrink-0 shadow-xs"
                                                                        onClick={(e) => {
                                                                            e.stopPropagation();
                                                                            handleApplyAllScraped(partner);
                                                                        }}
                                                                    >
                                                                        Apply All Updates
                                                                    </Button>
                                                                </div>
                                                            )}

                                                            {/* Practice */}
                                                            {(partner.practice || diffs.find(d => d.key === 'practice')) && (
                                                                <div className="space-y-1">
                                                                    <div className="flex justify-between items-center group/item hover:bg-white px-1 rounded transition-colors">
                                                                        <span className="text-slate-500 flex items-center gap-2">🏥 <span className="hidden sm:inline">Practice</span></span>
                                                                        <span className="font-medium text-slate-900 text-right">{partner.practice || <span className="text-slate-400 italic">None</span>}</span>
                                                                    </div>
                                                                    {(() => {
                                                                        const d = diffs.find(x => x.key === 'practice');
                                                                        if (!d) return null;
                                                                        return (
                                                                            <div className="p-1.5 rounded-md bg-emerald-50/70 border border-emerald-200/80 flex items-center justify-between gap-2 text-xs">
                                                                                <div className="flex items-center gap-1.5 text-emerald-900 truncate">
                                                                                    <span className="text-emerald-700 font-semibold shrink-0">↳ 🌐 Directory:</span>
                                                                                    <span className="truncate font-medium">{d.scrapedValue}</span>
                                                                                </div>
                                                                                <Button
                                                                                    size="sm"
                                                                                    variant="outline"
                                                                                    className="h-5 px-2 text-xs text-emerald-800 bg-white hover:bg-emerald-100 border-emerald-300 rounded font-medium shrink-0"
                                                                                    onClick={(e) => {
                                                                                        e.stopPropagation();
                                                                                        handleApplyScrapedField(partner, d);
                                                                                    }}
                                                                                >
                                                                                    Apply
                                                                                </Button>
                                                                            </div>
                                                                        );
                                                                    })()}
                                                                </div>
                                                            )}

                                                            {/* Distance / Miles */}
                                                            {(partner.distance || diffs.find(d => d.key === 'distance')) && (
                                                                <div className="space-y-1">
                                                                    <div className="flex justify-between items-center group/item hover:bg-white px-1 rounded transition-colors">
                                                                        <span className="text-slate-500 flex items-center gap-2">📏 <span className="hidden sm:inline">Distance</span></span>
                                                                        <span className="font-medium text-slate-900 text-right">{partner.distance || <span className="text-slate-400 italic">None</span>}</span>
                                                                    </div>
                                                                    {(() => {
                                                                        const d = diffs.find(x => x.key === 'distance');
                                                                        if (!d) return null;
                                                                        return (
                                                                            <div className="p-1.5 rounded-md bg-emerald-50/70 border border-emerald-200/80 flex items-center justify-between gap-2 text-xs">
                                                                                <div className="flex items-center gap-1.5 text-emerald-900 truncate">
                                                                                    <span className="text-emerald-700 font-semibold shrink-0">↳ 🌐 Directory:</span>
                                                                                    <span className="truncate font-medium">{d.scrapedValue}</span>
                                                                                </div>
                                                                                <Button
                                                                                    size="sm"
                                                                                    variant="outline"
                                                                                    className="h-5 px-2 text-xs text-emerald-800 bg-white hover:bg-emerald-100 border-emerald-300 rounded font-medium shrink-0"
                                                                                    onClick={(e) => {
                                                                                        e.stopPropagation();
                                                                                        handleApplyScrapedField(partner, d);
                                                                                    }}
                                                                                >
                                                                                    Apply
                                                                                </Button>
                                                                            </div>
                                                                        );
                                                                    })()}
                                                                </div>
                                                            )}

                                                            {/* Location / Address */}
                                                            {(partner.address || partner.city || partner.state || partner.country || diffs.find(d => d.key === 'address')) && (
                                                                <div className="space-y-1">
                                                                    <div className="flex justify-between items-start group/item hover:bg-white px-1 rounded transition-colors">
                                                                        <span className="text-slate-500 flex items-center gap-2 mt-0.5">📍 <span className="hidden sm:inline">Location</span></span>
                                                                        <span className="font-medium text-slate-900 text-right max-w-[65%] leading-tight">
                                                                            {[partner.address, partner.city, partner.state, partner.country].filter(Boolean).join(", ") || <span className="text-slate-400 italic">None</span>}
                                                                        </span>
                                                                    </div>
                                                                    {(() => {
                                                                        const d = diffs.find(x => x.key === 'address');
                                                                        if (!d) return null;
                                                                        return (
                                                                            <div className="p-1.5 rounded-md bg-emerald-50/70 border border-emerald-200/80 flex items-center justify-between gap-2 text-xs">
                                                                                <div className="flex items-center gap-1.5 text-emerald-900 truncate">
                                                                                    <span className="text-emerald-700 font-semibold shrink-0">↳ 🌐 Directory:</span>
                                                                                    <span className="truncate font-medium">{d.scrapedValue}</span>
                                                                                </div>
                                                                                <Button
                                                                                    size="sm"
                                                                                    variant="outline"
                                                                                    className="h-5 px-2 text-xs text-emerald-800 bg-white hover:bg-emerald-100 border-emerald-300 rounded font-medium shrink-0"
                                                                                    onClick={(e) => {
                                                                                        e.stopPropagation();
                                                                                        handleApplyScrapedField(partner, d);
                                                                                    }}
                                                                                >
                                                                                    Apply
                                                                                </Button>
                                                                            </div>
                                                                        );
                                                                    })()}
                                                                </div>
                                                            )}

                                                            {/* Phone */}
                                                            {(partner.phone || diffs.find(d => d.key === 'phone')) && (
                                                                <div className="space-y-1">
                                                                    <div className="flex justify-between items-center group/item hover:bg-white px-1 rounded transition-colors">
                                                                        <span className="text-slate-500 flex items-center gap-2">📞 <span className="hidden sm:inline">Phone</span></span>
                                                                        <span className="font-medium text-slate-900 text-right">{partner.phone || <span className="text-slate-400 italic">None</span>}</span>
                                                                    </div>
                                                                    {(() => {
                                                                        const d = diffs.find(x => x.key === 'phone');
                                                                        if (!d) return null;
                                                                        return (
                                                                            <div className="p-1.5 rounded-md bg-emerald-50/70 border border-emerald-200/80 flex items-center justify-between gap-2 text-xs">
                                                                                <div className="flex items-center gap-1.5 text-emerald-900 truncate">
                                                                                    <span className="text-emerald-700 font-semibold shrink-0">↳ 🌐 Directory:</span>
                                                                                    <span className="truncate font-medium">{d.scrapedValue}</span>
                                                                                </div>
                                                                                <Button
                                                                                    size="sm"
                                                                                    variant="outline"
                                                                                    className="h-5 px-2 text-xs text-emerald-800 bg-white hover:bg-emerald-100 border-emerald-300 rounded font-medium shrink-0"
                                                                                    onClick={(e) => {
                                                                                        e.stopPropagation();
                                                                                        handleApplyScrapedField(partner, d);
                                                                                    }}
                                                                                >
                                                                                    Apply
                                                                                </Button>
                                                                            </div>
                                                                        );
                                                                    })()}
                                                                </div>
                                                            )}

                                                            {partner.languages && (
                                                                <div className="flex justify-between items-center group/item hover:bg-white px-1 rounded transition-colors">
                                                                    <span className="text-slate-500 flex items-center gap-2">🗣️ <span className="hidden sm:inline">Languages</span></span>
                                                                    <span className="font-medium text-slate-900 text-right">
                                                                        {Array.isArray(partner.languages) ? partner.languages.join(", ") : partner.languages}
                                                                    </span>
                                                                </div>
                                                            )}
                                                            {!!partner.expertiseCount && (
                                                                <div className="flex justify-between items-center group/item hover:bg-white px-1 rounded transition-colors">
                                                                    <span className="text-slate-500 flex items-center gap-2">🎓 <span className="hidden sm:inline">Expertise</span></span>
                                                                    <span className="font-medium text-slate-900 text-right">{partner.expertiseCount} Areas</span>
                                                                </div>
                                                            )}
                                                            {partner.acceptingNewPatients !== undefined && (
                                                                <div className="flex justify-between items-center group/item hover:bg-white px-1 rounded transition-colors">
                                                                    <span className="text-slate-500 flex items-center gap-2">📝 <span className="hidden sm:inline">Accepting</span></span>
                                                                    <span className={`font-medium text-right px-2 py-0.5 rounded-full text-xs ${partner.acceptingNewPatients ? "bg-green-100 text-green-700 border border-green-200" : "bg-amber-100 text-amber-700 border border-amber-200"}`}>
                                                                        {partner.acceptingNewPatients ? "✅ Yes" : "❌ No"}
                                                                    </span>
                                                                </div>
                                                            )}

                                                            {/* Bio */}
                                                            {(partner.bio || diffs.find(d => d.key === 'bio')) && (
                                                                <div className="space-y-1 pt-2 border-t border-slate-200/60 mt-2">
                                                                    <div className="flex flex-col gap-1">
                                                                        <span className="text-slate-500 text-xs uppercase tracking-wider font-semibold">About</span>
                                                                        <p className="font-medium text-slate-700 text-xs leading-relaxed italic bg-white p-2 rounded border border-slate-100">
                                                                            "{partner.bio || 'No biography provided'}"
                                                                        </p>
                                                                    </div>
                                                                    {(() => {
                                                                        const d = diffs.find(x => x.key === 'bio');
                                                                        if (!d) return null;
                                                                        return (
                                                                            <div className="p-2 rounded-md bg-emerald-50/70 border border-emerald-200/80 flex flex-col gap-1.5 text-xs">
                                                                                <div className="flex items-center justify-between gap-2">
                                                                                    <span className="text-emerald-700 font-semibold">↳ 🌐 Directory Bio:</span>
                                                                                    <Button
                                                                                        size="sm"
                                                                                        variant="outline"
                                                                                        className="h-5 px-2 text-xs text-emerald-800 bg-white hover:bg-emerald-100 border-emerald-300 rounded font-medium shrink-0"
                                                                                        onClick={(e) => {
                                                                                            e.stopPropagation();
                                                                                            handleApplyScrapedField(partner, d);
                                                                                        }}
                                                                                    >
                                                                                        Apply Bio
                                                                                    </Button>
                                                                                </div>
                                                                                <p className="font-medium text-emerald-900 text-xs italic bg-white/80 p-1.5 rounded border border-emerald-100">
                                                                                    "{d.scrapedValue}"
                                                                                </p>
                                                                            </div>
                                                                        );
                                                                    })()}
                                                                </div>
                                                            )}
                                                        </div>
                                                    </AccordionContent>
                                                )}
                                            </AccordionItem>
                                        );
                                    };

                                    return (
                                        <>
                                            {/* ⭐ Trusted Partners Section */}
                                            {trustedSection.length > 0 && (
                                                <div className="space-y-3 sm:space-y-4 bg-amber-50/40 p-3 sm:p-5 rounded-xl sm:rounded-2xl border border-amber-200/80 shadow-xs relative overflow-hidden">
                                                    <div className="absolute top-0 left-0 w-1.5 h-full bg-amber-400"></div>
                                                    <h3 className="text-xs sm:text-sm font-semibold text-amber-900 border-b border-amber-200/80 pb-2 flex flex-col sm:flex-row sm:items-center justify-between gap-1">
                                                        <span className="flex items-center gap-2">
                                                            <Star className="h-4 w-4 fill-amber-400 text-amber-500" /> Trusted Partners
                                                            <span className="text-xs bg-amber-100 text-amber-800 font-bold px-2 py-0.5 rounded-full border border-amber-200">
                                                                {trustedSection.length}
                                                            </span>
                                                        </span>
                                                        <span className="text-xs font-normal text-amber-700">
                                                            High priority for referrals
                                                        </span>
                                                    </h3>
                                                    <Accordion type="single" collapsible className="w-full grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-x-3.5 gap-y-3.5 sm:gap-y-8 pt-2 sm:pt-6 items-start relative z-10">
                                                        {trustedSection.map((p, i) => renderPartnerCard(p, `trusted-${i}`))}
                                                    </Accordion>
                                                </div>
                                            )}

                                            {/* 🏢 Same Practice Partners Section */}
                                            {samePracticeSection.length > 0 && (
                                                <div className="space-y-3 sm:space-y-4 bg-sky-50/40 p-3 sm:p-5 rounded-xl sm:rounded-2xl border border-sky-200/80 shadow-xs relative overflow-hidden">
                                                    <div className="absolute top-0 left-0 w-1.5 h-full bg-sky-400"></div>
                                                    <h3 className="text-xs sm:text-sm font-semibold text-sky-900 border-b border-sky-200/80 pb-2 flex flex-col sm:flex-row sm:items-center justify-between gap-1">
                                                        <span className="flex items-center gap-2">
                                                            🏢 Same Practice Partners
                                                            <span className="text-xs bg-sky-100 text-sky-800 font-bold px-2 py-0.5 rounded-full border border-sky-200">
                                                                {samePracticeSection.length}
                                                            </span>
                                                        </span>
                                                        <span className="text-xs font-normal text-sky-700">
                                                            Providers from your practice
                                                        </span>
                                                    </h3>
                                                    <Accordion type="single" collapsible className="w-full grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-x-3.5 gap-y-3.5 sm:gap-y-8 pt-2 sm:pt-6 items-start relative z-10">
                                                        {samePracticeSection.map((p, i) => renderPartnerCard(p, `same-practice-${i}`))}
                                                    </Accordion>
                                                </div>
                                            )}

                                            {/* 🏢 Other External Providers Section */}
                                            {(() => {
                                                if (externalSection.length === 0) return null;

                                                // Group by practice
                                                const groups = {};
                                                externalSection.forEach(p => {
                                                    const practiceName = (p && typeof p === 'object' && p.practice && p.practice.trim()) || "Other External Providers";
                                                    if (!groups[practiceName]) groups[practiceName] = [];
                                                    groups[practiceName].push(p);
                                                });

                                                // Sort groups
                                                const sortedGroupNames = Object.keys(groups).sort((a, b) => {
                                                    if (a === "Other External Providers") return 1;
                                                    if (b === "Other External Providers") return -1;
                                                    return a.localeCompare(b);
                                                });

                                                const parseDistance = (distStr) => {
                                                    if (!distStr) return Infinity;
                                                    const match = String(distStr).match(/[\d.]+/);
                                                    return match ? parseFloat(match[0]) : Infinity;
                                                };

                                                return (
                                                    <div className="space-y-6">
                                                        <h3 className="text-xs sm:text-sm font-semibold text-slate-800 border-b border-slate-200 pb-2 flex flex-col sm:flex-row sm:items-center justify-between gap-1">
                                                            <span>External Providers</span>
                                                            <span className="text-xs font-normal text-slate-500">Click ⭐ for Trusted • 🏢 for Same Practice</span>
                                                        </h3>
                                                        {sortedGroupNames.map(groupName => {
                                                            const sortedProviders = groups[groupName].sort((a, b) => {
                                                                const distA = parseDistance(a?.distance);
                                                                const distB = parseDistance(b?.distance);
                                                                return distA - distB;
                                                            });

                                                            return (
                                                                <div key={groupName} className="space-y-3">
                                                                    <div className="flex items-center gap-2">
                                                                        <div className="h-4 w-1 bg-blue-500 rounded-full"></div>
                                                                        <h4 className="text-xs font-semibold text-slate-700 uppercase tracking-wider">{groupName}</h4>
                                                                        {groupName !== "Other External Providers" && (
                                                                            <span className="text-xs font-medium bg-slate-100 text-slate-500 px-1.5 py-0.5 rounded-full">{sortedProviders.length}</span>
                                                                        )}
                                                                    </div>
                                                                    <Accordion type="single" collapsible className="w-full grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-x-3.5 gap-y-3.5 sm:gap-y-8 pt-2 sm:pt-6 items-start">
                                                                        {sortedProviders.map((p, i) => renderPartnerCard(p, `external-${groupName}-${i}`))}
                                                                    </Accordion>
                                                                </div>
                                                            );
                                                        })}
                                                    </div>
                                                );
                                            })()}

                                            {externalPartnersList.length > visibleCount && (
                                                <div ref={loadMoreRef} className="col-span-full flex justify-center py-4">
                                                    <span className="text-xs text-slate-400 animate-pulse">Loading more external providers...</span>
                                                </div>
                                            )}
                                        </>
                                    );
                                })()
                            ) : (
                                <div className="flex h-32 flex-col items-center justify-center text-center text-slate-400">
                                    <p className="text-sm">No practice partners found.</p>
                                    <p className="text-xs mt-1">
                                        Try syncing nearby doctors or adding manually.
                                    </p>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </CardContent>
        </Card>
    );
}
